import React, { useState, useEffect, useRef, useMemo } from "react";
import ReactDOM from "react-dom";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useTheme } from "../contexts/ThemeContext";
import "./WorkspaceAnalytics.css";
import "./WorkspaceUsers.css";

// Modal animation variants
const modalOverlayVariants = {
	hidden: { opacity: 0 },
	visible: { 
		opacity: 1,
		transition: { duration: 0.2, ease: "easeOut" }
	},
	exit: { 
		opacity: 0,
		transition: { duration: 0.15, ease: "easeIn" }
	}
};

const modalContentVariants = {
	hidden: { 
		opacity: 0, 
		scale: 0.95,
		y: 20
	},
	visible: { 
		opacity: 1, 
		scale: 1,
		y: 0,
		transition: { 
			duration: 0.25, 
			ease: [0.4, 0, 0.2, 1]
		}
	},
	exit: { 
		opacity: 0, 
		scale: 0.95,
		y: 10,
		transition: { 
			duration: 0.15, 
			ease: "easeIn"
		}
	}
};

// Animation variants for staggered animations
const containerVariants = {
	hidden: { opacity: 0 },
	visible: {
		opacity: 1,
		transition: {
			staggerChildren: 0.1,
			delayChildren: 0.1
		}
	}
};

const itemVariants = {
	hidden: { opacity: 0, y: 20 },
	visible: {
		opacity: 1,
		y: 0,
		transition: {
			duration: 0.5,
			ease: [0.4, 0, 0.2, 1]
		}
	}
};

const cardVariants = {
	hidden: { opacity: 0, y: 30, scale: 0.95 },
	visible: {
		opacity: 1,
		y: 0,
		scale: 1,
		transition: {
			duration: 0.5,
			ease: [0.4, 0, 0.2, 1]
		}
	},
	hover: {
		y: -4,
		scale: 1.02,
		transition: {
			duration: 0.3,
			ease: [0.4, 0, 0.2, 1]
		}
	}
};

// Simplified - no glow orbs for cleaner design
const GlowOrbs = () => null;
const ActiveUsersModal = ({ isOpen, onClose, activeUsers, modalType = "DAU", selectedTimePoint, isLoading = false }) => {
	// Auto-expand workspace distribution for workspace modal types and inactive companies
	const [isWorkspaceDistributionExpanded, setIsWorkspaceDistributionExpanded] = useState(
		modalType === "WORKSPACE_ACTIVE" || modalType === "WORKSPACE_INACTIVE" || modalType === "INACTIVE_COMPANIES"
	);

	// Auto-expand workspace distribution when modal type changes to workspace types and inactive companies
	useEffect(() => {
		if (modalType === "WORKSPACE_ACTIVE" || modalType === "WORKSPACE_INACTIVE" || modalType === "INACTIVE_COMPANIES") {
			setIsWorkspaceDistributionExpanded(true);
		}
	}, [modalType]);

	// Prevent background scrolling when modal is open
	useEffect(() => {
		if (isOpen) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "unset";
		}

		return () => {
			document.body.style.overflow = "unset";
		};
	}, [isOpen]);

	// Group users by workspace and calculate counts
	const workspaceGroups = useMemo(() => {
		if (!activeUsers || activeUsers.length === 0) return {};
		
		const groups = {};
		activeUsers.forEach(user => {
			const workspaceName = user.workspaceName || 'Unknown';
			if (!groups[workspaceName]) {
				groups[workspaceName] = {
					users: [],
					count: 0,
					totalDMs: 0,
					totalKnocks: 0,
					totalInvites: 0
				};
			}
			groups[workspaceName].users.push(user);
			groups[workspaceName].count++;
			groups[workspaceName].totalDMs += user.dmCount || 0;
			groups[workspaceName].totalKnocks += user.totalKnocks || 0;
			groups[workspaceName].totalInvites += user.totalInvites || 0;
		});
		
		// Sort workspaces by name
		const sortedWorkspaces = {};
		Object.keys(groups).sort().forEach(key => {
			sortedWorkspaces[key] = groups[key];
			// Sort users within each workspace by name
			sortedWorkspaces[key].users.sort((a, b) => 
				(a.userName || '').localeCompare(b.userName || '')
			);
		});
		
		return sortedWorkspaces;
	}, [activeUsers]);

	// Use portal to render modal outside the component tree to avoid z-index stacking context issues
	if (!isOpen) return null;
	
	return ReactDOM.createPortal(
		<motion.div 
			className="modal-overlay" 
			onClick={onClose}
			variants={modalOverlayVariants}
			initial="hidden"
			animate="visible"
			exit="exit"
		>
			<motion.div 
				className="modal-content" 
				onClick={(e) => e.stopPropagation()}
				variants={modalContentVariants}
				initial="hidden"
				animate="visible"
				exit="exit"
			>
				<div className="modal-header">
					<h3>
						{modalType === "DAU" ? "Daily Active Users (DAU)" : 
						 modalType === "MAU" ? "Monthly Active Users (MAU)" : 
						 modalType === "TIME_SPECIFIC" ? "Active Users at Specific Time" :
						 modalType === "WORKSPACE_ACTIVE" ? "Active Workspace Users" :
						 modalType === "WORKSPACE_INACTIVE" ? "Inactive Workspace Users" :
						 modalType === "INACTIVE_COMPANIES" ? "Inactive Companies Distribution" :
						 "Currently Active Users"}
					</h3>
					{(modalType === "TIME_SPECIFIC" || modalType === "WORKSPACE_ACTIVE" || modalType === "WORKSPACE_INACTIVE" || modalType === "INACTIVE_COMPANIES") && selectedTimePoint && (
						<div style={{ fontSize: '14px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
							{modalType === "TIME_SPECIFIC" 
								? new Date(selectedTimePoint).toLocaleString("en-US", {
									weekday: 'short',
									month: 'short',
									day: 'numeric',
									hour: 'numeric',
									minute: '2-digit',
									hour12: true
								})
								: new Date(selectedTimePoint).toLocaleDateString("en-US", {
									weekday: 'long',
									year: 'numeric',
									month: 'long',
									day: 'numeric'
								})
							}
						</div>
					)}
					<button className="close-button" onClick={onClose}>
						×
					</button>
				</div>
				<div className="modal-body">
					{isLoading ? (
						<div style={{ 
							display: 'flex', 
							flexDirection: 'column', 
							alignItems: 'center', 
							justifyContent: 'center', 
							padding: '3rem',
							gap: '1rem'
						}}>
							<div className="loading-spinner" style={{ width: '40px', height: '40px' }}></div>
							<p style={{ color: '#71717a', margin: 0 }}>Loading data...</p>
						</div>
					) : !activeUsers || activeUsers.length === 0 ? (
						<p>No data available {modalType === "DAU" ? "today" : modalType === "MAU" ? "this month" : modalType === "TIME_SPECIFIC" ? "at this time" : modalType === "WORKSPACE_ACTIVE" ? "for active workspaces" : modalType === "WORKSPACE_INACTIVE" ? "for inactive workspaces" : modalType === "INACTIVE_COMPANIES" ? "for inactive companies" : "currently online"}</p>
					) : (
						<>
							{/* Collapsible workspace distribution */}
							<div className="workspace-summary" style={{ 
								marginBottom: '25px', 
								backgroundColor: 'var(--bg-quaternary)', 
								borderRadius: '12px',
								border: '1px solid var(--border-strong)'
							}}>
								{/* Dropdown header */}
								<div 
									onClick={() => setIsWorkspaceDistributionExpanded(!isWorkspaceDistributionExpanded)}
									style={{
										padding: '16px 20px',
										cursor: 'pointer',
										display: 'flex',
										justifyContent: 'space-between',
										alignItems: 'center',
										borderRadius: '12px',
										transition: 'all 0.2s ease',
										backgroundColor: isWorkspaceDistributionExpanded ? 'var(--border-light)' : 'transparent'
									}}
								>
									<div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
										<span style={{ 
											fontSize: '16px', 
											color: 'var(--text-secondary)',
											transition: 'transform 0.2s ease',
											transform: isWorkspaceDistributionExpanded ? 'rotate(90deg)' : 'rotate(0deg)'
										}}>
											▶
										</span>
										<h4 style={{ 
											margin: '0', 
											color: 'var(--text-primary)', 
											fontSize: '16px',
											fontWeight: '600',
											letterSpacing: '-0.02em'
										}}>
											Workspace Distribution
										</h4>
									</div>
									<div style={{
										fontSize: '14px',
										color: 'var(--text-tertiary)',
										fontWeight: '500'
									}}>
										{modalType === "WORKSPACE_INACTIVE" 
											? `${Object.keys(workspaceGroups).length} workspaces`
											: `${Object.keys(workspaceGroups).length} workspaces • ${activeUsers.length} ${modalType === "DAU" ? "daily" : modalType === "MAU" ? "monthly" : modalType === "TIME_SPECIFIC" ? "active" : modalType === "WORKSPACE_ACTIVE" ? "active" : "online"} users`
										}
									</div>
								</div>

								{/* Collapsible content */}
								{isWorkspaceDistributionExpanded && (
									<div style={{ 
										padding: '0 20px 20px 20px',
										borderTop: '1px solid var(--border-strong)',
										marginTop: '0'
									}}>
										<div style={{ 
											display: 'grid', 
											gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
											gap: '12px',
											marginTop: '16px'
										}}>
											{Object.entries(workspaceGroups)
												.sort(([, a], [, b]) => b.count - a.count)
												.map(([workspaceName, group]) => (
												<div key={workspaceName} style={{
													padding: '14px 16px',
													backgroundColor: 'var(--bg-tertiary)',
													border: '1px solid var(--border-strong)',
													borderRadius: '8px',
													display: 'flex',
													justifyContent: 'space-between',
													alignItems: 'center',
													transition: 'all 0.2s ease',
													boxShadow: 'var(--shadow-light)',
													cursor: 'default'
												}}>
													<div style={{ 
														flex: 1, 
														marginRight: '12px',
														overflow: 'hidden'
													}}>
														<div style={{ 
															fontSize: '14px', 
															color: 'var(--text-primary)',
															fontWeight: '500',
															lineHeight: '1.3',
															overflow: 'hidden',
															textOverflow: 'ellipsis',
															whiteSpace: 'nowrap'
														}}>
															{workspaceName}
														</div>
													</div>
													{/* Show user count for all modals except inactive workspaces and inactive companies */}
													{modalType !== "WORKSPACE_INACTIVE" && modalType !== "INACTIVE_COMPANIES" && (
														<div style={{
															backgroundColor: group.count > 10 ? '#28a745' : group.count > 5 ? '#007bff' : '#6c757d',
															color: 'white',
															padding: '6px 12px',
															borderRadius: '16px',
															fontSize: '12px',
															fontWeight: '600',
															minWidth: 'fit-content',
															textAlign: 'center',
															whiteSpace: 'nowrap'
														}}>
															{group.count} user{group.count !== 1 ? 's' : ''}
														</div>
													)}
												</div>
											))}
										</div>
									</div>
								)}
							</div>

							{/* Original table with all users - Hidden for workspace modals and inactive companies */}
							{modalType !== "WORKSPACE_ACTIVE" && modalType !== "WORKSPACE_INACTIVE" && modalType !== "INACTIVE_COMPANIES" && (
								<div className="table-wrapper">
									<table className="workspace-table">
										<thead>
											<tr>
												<th>Name</th>
												<th>Email</th>
												<th>Role</th>
												<th>Workspace</th>
												<th>DM Count</th>
												<th>Total Knocks</th>
												<th>Total Invites</th>
											</tr>
										</thead>
										<tbody>
											{[...activeUsers]
												.sort((a, b) => {
													const workspaceA = (a.workspaceName || '').toLowerCase();
													const workspaceB = (b.workspaceName || '').toLowerCase();
													return workspaceA.localeCompare(workspaceB);
												})
												.map((user, index) => (
													<tr key={`${user.userEmail}-${index}`}>
														<td className="user-name">
															{user.userName}
														</td>
														<td className="user-email">
															{user.userEmail}
														</td>
														<td className={`user-role ${user.userType?.toLowerCase()}`}>
															{user.userType}
														</td>
														<td className="user-workspace">
															{user.workspaceName}
														</td>
														<td className="user-dm-count">
															{user.dmCount || 0}
														</td>
														<td className="user-knocks">
															{user.totalKnocks || 0}
														</td>
														<td className="user-invites">
															{user.totalInvites || 0}
														</td>
													</tr>
												))}
										</tbody>
									</table>
								</div>
							)}
						</>
					)}
				</div>
			</motion.div>
		</motion.div>,
		document.body
	);
};

const WorkspaceAnalytics = () => {
	// All state declarations at the top
	const [hourlyData, setHourlyData] = useState([]);
	const [loadingHourlyData, setLoadingHourlyData] = useState(true);
	const [dailySessionData, setDailySessionData] = useState([]);
	const [isActiveUsersModalOpen, setIsActiveUsersModalOpen] = useState(false);
	const [dauUsers, setDauUsers] = useState([]);
	// eslint-disable-next-line no-unused-vars
	const { workspaceId, workspaceName } = useParams();
	// eslint-disable-next-line no-unused-vars
	const { isDarkMode } = useTheme();
	const [timeInterval, setTimeInterval] = useState("30min");
	const [dailyInteractionData, setDailyInteractionData] = useState([]);
	// eslint-disable-next-line no-unused-vars
	const [isLoading, setIsLoading] = useState(false);
	const [isDailyInteractionsLoading, setIsDailyInteractionsLoading] = useState(false);
	const [leaderboardData, setLeaderboardData] = useState([]);
	const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);
	const [workspaceActivityData, setWorkspaceActivityData] = useState([]);
	const [loadingWorkspaceActivity, setLoadingWorkspaceActivity] = useState(true);
	const [leaderboardFilter, setLeaderboardFilter] = useState("this-month"); // Changed from "all-time" for faster load
	const [dailyInteractionsFilter, setDailyInteractionsFilter] = useState("current-month");

	// Daily transcripts chart (mirrors the daily-interactions chart)
	const [dailyTranscriptData, setDailyTranscriptData] = useState([]);
	const [isDailyTranscriptsLoading, setIsDailyTranscriptsLoading] = useState(false);
	const [transcriptsFilter, setTranscriptsFilter] = useState("current-month");
	// Daily copilot responses chart (mirrors the transcripts chart)
	const [dailyCopilotData, setDailyCopilotData] = useState([]);
	const [isDailyCopilotLoading, setIsDailyCopilotLoading] = useState(false);
	const [copilotFilter, setCopilotFilter] = useState("current-month");

	// DAU Chart time range filter
	const [dauTimeRange, setDauTimeRange] = useState("1month"); // Default to 1 month for performance
	const [isDauLoading, setIsDauLoading] = useState(false);
	
	// Refs for request deduplication - prevents duplicate API calls
	const pendingRequests = useRef(new Map());
	
	// Track if initial batch load is complete
	const [initialLoadComplete, setInitialLoadComplete] = useState(false);
	
	// Workspace modal state - using ActiveUsersModal instead
	const [isWorkspaceModalOpen, setIsWorkspaceModalOpen] = useState(false);
	const [workspaceUsers, setWorkspaceUsers] = useState([]);
	const [workspaceModalType, setWorkspaceModalType] = useState("");
	const [selectedWorkspaceDate, setSelectedWorkspaceDate] = useState(null);

	// Move this here
	const [analyticsData, setAnalyticsData] = useState({
		monthlyData: [],
		dailyData: [],
		currentMetrics: {
			currentActiveUsers: 0,
			todayDAU: 0,
			yesterdayDAU: 0,
			currentMAU: 0,
			lastMonthMAU: 0,
			currentInteractions: 0,
			lastMonthInteractions: 0,
			currentDMs: 0,
			lastMonthDMs: 0,
		},
	});
	const [loading, setLoading] = useState(true);
	// eslint-disable-next-line no-unused-vars
	const [error, setError] = useState(null);
	const [timeRange, setTimeRange] = useState("6months"); // Default to 6 months for engagement chart
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isMauModalOpen, setIsMauModalOpen] = useState(false);
	const [activeUsers, setActiveUsers] = useState([]);
	const [liveStats, setLiveStats] = useState({
		onlineUsers: 0,
		liveWorkspaces: 0,
		degraded: false,
		updatedAt: null,
	});
	const [mauUsers, setMauUsers] = useState([]);
	// eslint-disable-next-line no-unused-vars
	const [workspaceInfo, setWorkspaceInfo] = useState(null);
	const [isTimeSpecificModalOpen, setIsTimeSpecificModalOpen] = useState(false);
	const [timeSpecificUsers, setTimeSpecificUsers] = useState([]);
	const [isModalLoading, setIsModalLoading] = useState(false);
	const [selectedTimePoint, setSelectedTimePoint] = useState(null);
	const calculateAverageTime = (data) => {
		if (!data || data.length === 0) return 0;
		// Filter out zero values and undefined
		const validData = data.filter((d) => d.totalSessionTime > 0);
		if (validData.length === 0) return 0;

		const sum = validData.reduce(
			(acc, curr) => acc + curr.totalSessionTime,
			0,
		);
		return Math.round(sum / validData.length);
	};
	useEffect(() => {
		if (hourlyData.length > 0 && analyticsData.currentMetrics) {
			const updatedData = [...hourlyData];
			updatedData[updatedData.length - 1] = {
				...updatedData[updatedData.length - 1],
				activeUsers:
					analyticsData.currentMetrics.currentActiveUsers || 2,
				timestamp: new Date().toISOString(),
			};
			setHourlyData(updatedData);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [analyticsData.currentMetrics?.currentActiveUsers]);

	const calculatePeakTime = (data) => {
		if (!data || data.length === 0) return 0;
		return Math.max(...data.map((d) => d.totalSessionTime));
	};

	const fetchDailySessionData = async () => {
		try {
			const response = await fetch(
				`${process.env.REACT_APP_BASE_URL}/workspaces/daily-sessions`,
			);
			const data = await response.json();
			setDailySessionData(data);
		} catch (error) {
			console.error("Error fetching daily session data:", error);
			// Set mock data for testing
			const mockData = Array.from({ length: 30 }, (_, i) => ({
				date: new Date(
					Date.now() - i * 24 * 60 * 60 * 1000,
				).toLocaleDateString("en-US", {
					month: "short",
					day: "numeric",
				}),
				totalSessionTime: Math.floor(Math.random() * 480) + 120, // Random time between 2-10 hours in minutes
			}));
			setDailySessionData(mockData);
		}
	};
	const formatTime = (minutes) => {
		const hours = Math.floor(minutes / 60);
		const mins = minutes % 60;
		return `${hours}h ${mins}m`;
	};

	// =====================================================
	// BATCH ANALYTICS FETCH - Single API call for all data
	// =====================================================
	const fetchBatchAnalytics = async () => {
		const requestKey = `batch-analytics-${timeRange}-${dauTimeRange}-${leaderboardFilter}-${dailyInteractionsFilter}-${timeInterval}`;
		if (pendingRequests.current.has(requestKey)) {
			console.log(`⏭️ Skipping duplicate batch request`);
			return pendingRequests.current.get(requestKey);
		}
		
		console.log(`🚀 Starting batch analytics fetch...`);
		const startTime = performance.now();
		
		try {
			// Set all loading states
			setLoading(true);
			setLoadingHourlyData(true);
			setLoadingLeaderboard(true);
			setLoadingWorkspaceActivity(true);
			setIsDailyInteractionsLoading(true);
			
			const fetchPromise = fetch(
				`${process.env.REACT_APP_BASE_URL}/workspaces/analytics-batch?` +
				`timeRange=${timeRange}&dailyRange=${dauTimeRange}` +
				`&leaderboardFilter=${leaderboardFilter}` +
				`&dailyInteractionsRange=${dailyInteractionsFilter}` +
				`&hourlyInterval=${timeInterval}`
			);
			pendingRequests.current.set(requestKey, fetchPromise);
			
			const response = await fetchPromise;
			if (!response.ok) {
				throw new Error("Failed to fetch batch analytics");
			}
			
			const data = await response.json();
			const endTime = performance.now();
			console.log(`✅ Batch analytics loaded in ${Math.round(endTime - startTime)}ms`);
			
			// Set all data at once - this triggers a single re-render
			console.log('📊 Batch response received:', {
				analytics: !!data.analytics,
				dailySessions: data.dailySessions?.length,
				hourlyActiveUsers: data.hourlyActiveUsers?.length,
				dailyInteractions: data.dailyInteractions?.length,
				leaderboard: data.leaderboard?.length,
				workspaceActivity: data.workspaceActivity,
			});
			
			// Validate and set analytics data
			if (data.analytics) {
				setAnalyticsData(data.analytics);
			}
			
			// Validate and set daily sessions data
			if (data.dailySessions && Array.isArray(data.dailySessions)) {
				console.log('📊 Setting daily sessions:', data.dailySessions.length, 'items');
				if (data.dailySessions.length > 0) {
					console.log('📊 Daily sessions sample:', JSON.stringify(data.dailySessions[0]));
				}
				setDailySessionData(data.dailySessions);
			} else {
				console.warn('⚠️ Daily sessions data missing or invalid:', data.dailySessions);
			}
			
			// Set hourly data
			if (data.hourlyActiveUsers && Array.isArray(data.hourlyActiveUsers)) {
				setHourlyData(data.hourlyActiveUsers);
			}
			
			// Set daily interactions
			if (data.dailyInteractions && Array.isArray(data.dailyInteractions)) {
				setDailyInteractionData(data.dailyInteractions);
			}
			
			// Set leaderboard
			if (data.leaderboard && Array.isArray(data.leaderboard)) {
				setLeaderboardData(data.leaderboard);
			}
			
			// Extract workspace activity data from response
			let wsActivityData = [];
			if (data.workspaceActivity?.data && Array.isArray(data.workspaceActivity.data)) {
				wsActivityData = data.workspaceActivity.data;
			} else if (Array.isArray(data.workspaceActivity)) {
				// Handle case where workspaceActivity is directly an array
				wsActivityData = data.workspaceActivity;
			}
			console.log('📊 Setting workspace activity data:', wsActivityData.length, 'items');
			if (wsActivityData.length > 0) {
				console.log('📊 First item:', JSON.stringify(wsActivityData[0]));
				console.log('📊 Last item:', JSON.stringify(wsActivityData[wsActivityData.length - 1]));
			}
			setWorkspaceActivityData(wsActivityData);
			
			// Clear all loading states
			setLoading(false);
			setLoadingHourlyData(false);
			setLoadingLeaderboard(false);
			setLoadingWorkspaceActivity(false);
			setIsDailyInteractionsLoading(false);
			setInitialLoadComplete(true);
			
		} catch (err) {
			console.error("❌ Error fetching batch analytics:", err);
			// Fallback to individual fetches
			console.log("📋 Falling back to individual fetches...");
			setInitialLoadComplete(true);
			fetchAnalyticsData();
			fetchDailySessionData();
			fetchHourlyActiveUsers();
			fetchDailyInteractionData(dailyInteractionsFilter);
			fetchDailyTranscriptData(transcriptsFilter);
			fetchDailyCopilotData(copilotFilter);
			fetchLeaderboardData(leaderboardFilter);
			fetchWorkspaceActivityData();
		} finally {
			pendingRequests.current.delete(requestKey);
		}
	};

	// Fetch live workspace stats for the active users card sub-line
	const fetchLiveStats = async () => {
		try {
			const response = await fetch(`${process.env.REACT_APP_BASE_URL}/workspaces/stats`);
			if (response.ok) {
				const data = await response.json();
				const liveWorkspaces = data.filter(w => w.activeUsers > 0);
				const onlineUsers = liveWorkspaces.reduce((sum, w) => sum + (w.activeUsers || 0), 0);
				const degraded = data.some(w => w.activeUsersDegraded);
				setLiveStats({
					onlineUsers,
					liveWorkspaces: liveWorkspaces.length,
					degraded,
					updatedAt: Date.now(),
				});
			}
		} catch (err) {
			console.error("Error fetching live stats:", err);
		}
	};

	// Initial load + 15s polling — fetchLiveStats was previously called once
	// at mount, leaving the count frozen until reload. Polling closes that gap;
	// the visibility check avoids hammering the API for hidden tabs.
	useEffect(() => {
		fetchBatchAnalytics();
		fetchWorkspaceInfo();
		fetchLiveStats();

		const tick = () => {
			if (typeof document === "undefined" || document.visibilityState === "visible") {
				fetchLiveStats();
			}
		};
		const intervalId = setInterval(tick, 15_000);
		const onVisibilityChange = () => {
			if (document.visibilityState === "visible") fetchLiveStats();
		};
		if (typeof document !== "undefined") {
			document.addEventListener("visibilitychange", onVisibilityChange);
		}
		return () => {
			clearInterval(intervalId);
			if (typeof document !== "undefined") {
				document.removeEventListener("visibilitychange", onVisibilityChange);
			}
		};
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Handle filter changes AFTER initial load - use individual endpoints
	useEffect(() => {
		if (initialLoadComplete) {
			fetchAnalyticsData();
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [timeRange, dauTimeRange]);

	useEffect(() => {
		fetchWorkspaceInfo();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [workspaceId]);
	const fetchAnalyticsData = async () => {
		// Request deduplication - prevent duplicate calls while request is in flight
		const requestKey = `analytics-${timeRange}-${dauTimeRange}`;
		if (pendingRequests.current.has(requestKey)) {
			console.log(`⏭️ Skipping duplicate request: ${requestKey}`);
			return pendingRequests.current.get(requestKey);
		}
		
		try {
			// Fetch real analytics data from API with dynamic daily range
			const fetchPromise = fetch(
				`${process.env.REACT_APP_BASE_URL}/workspaces/analytics?timeRange=${timeRange}&dailyRange=${dauTimeRange}`,
			);
			pendingRequests.current.set(requestKey, fetchPromise);
			
			const response = await fetchPromise;
			if (!response.ok) {
				throw new Error("Failed to fetch analytics data");
			}
			const data = await response.json();
			setAnalyticsData(data);
			setLoading(false);
		} catch (err) {
			console.error("Error fetching analytics data:", err);
			// Fallback to mock data if API fails
			const mockData = generateMockAnalyticsData(dauTimeRange);
			setAnalyticsData(mockData);
			setLoading(false);
		} finally {
			pendingRequests.current.delete(requestKey);
		}
	};

	// Custom handler for DAU time range changes with loading state
	const handleDauTimeRangeChange = async (newRange) => {
		if (newRange === dauTimeRange) return; // Don't reload if same range
		
		setIsDauLoading(true);
		setDauTimeRange(newRange);
		
		try {
			// Small delay to show loading state
			await new Promise(resolve => setTimeout(resolve, 100));
			
			// Fetch new data with updated range
			const response = await fetch(
				`${process.env.REACT_APP_BASE_URL}/workspaces/analytics?timeRange=${timeRange}&dailyRange=${newRange}`,
			);
			if (!response.ok) {
				throw new Error("Failed to fetch analytics data");
			}
			const data = await response.json();
			setAnalyticsData(data);
		} catch (err) {
			console.error("Error fetching DAU data:", err);
			// Fallback to mock data if API fails
			const mockData = generateMockAnalyticsData(newRange);
			setAnalyticsData(mockData);
		} finally {
			setIsDauLoading(false);
		}
	};

	// Update fetchHourlyActiveUsers to use analyticsData safely
	const fetchHourlyActiveUsers = async () => {
		// Request deduplication - prevent duplicate calls while request is in flight
		const requestKey = `hourly-active-users-${timeInterval}`;
		if (pendingRequests.current.has(requestKey)) {
			console.log(`⏭️ Skipping duplicate request: ${requestKey}`);
			return pendingRequests.current.get(requestKey);
		}
		
		try {
			setLoadingHourlyData(true);
			const fetchPromise = fetch(
				`${process.env.REACT_APP_BASE_URL}/workspaces/hourly-active-users?interval=${timeInterval}`,
			);
			pendingRequests.current.set(requestKey, fetchPromise);
			
			const response = await fetchPromise;
			if (!response.ok) {
				throw new Error("Failed to fetch hourly active users");
			}
			const data = await response.json();

			// Process the data to ensure it has the correct structure
			const processedData = data.map(item => ({
				...item,
				timestamp: item.timestamp || new Date(item.time).toISOString(),
				activeUsers: item.activeUsers || 0
			}));

			// Add current active users count to the latest data point
			if (processedData.length > 0) {
				const currentActiveUsers =
					analyticsData.currentMetrics?.currentActiveUsers || 0;
				const latestTimestamp = new Date().toISOString();

				// Update the last data point with actual current users
				processedData[processedData.length - 1] = {
					...processedData[processedData.length - 1],
					activeUsers:
						currentActiveUsers || processedData[processedData.length - 1].activeUsers,
					timestamp: latestTimestamp,
				};
			}

			setHourlyData(processedData);
		} catch (error) {
			console.error("Error fetching hourly active users:", error);
			const mockHourlyData = generateMockHourlyData();
			setHourlyData(mockHourlyData);
		} finally {
			pendingRequests.current.delete(requestKey);
			setLoadingHourlyData(false);
		}
	};

	// Add this useEffect to refetch when interval changes (after the existing useEffects)
	useEffect(() => {
		if (initialLoadComplete) {
			fetchHourlyActiveUsers();
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [timeInterval]);
	const fetchActiveUsers = async () => {
		// Open modal immediately with loading state
		setActiveUsers([]);
		setIsModalLoading(true);
		setIsActiveUsersModalOpen(true);
		
		try {
			const response = await fetch(
				`${process.env.REACT_APP_BASE_URL}/workspaces/active-users`,
			);
			if (!response.ok) {
				throw new Error("Failed to fetch active users");
			}
			const data = await response.json();
			setActiveUsers(data);
		} catch (error) {
			console.error("Error fetching active users:", error);
		} finally {
			setIsModalLoading(false);
		}
	};
	const fetchDauUsers = async () => {
		// Open modal immediately with loading state
		setDauUsers([]);
		setIsModalLoading(true);
		setIsModalOpen(true);
		
		try {
			const response = await fetch(
				`${process.env.REACT_APP_BASE_URL}/workspaces/dau-users`,
			);
			if (!response.ok) {
				throw new Error("Failed to fetch DAU users");
			}
			const data = await response.json();
			setDauUsers(data);
		} catch (error) {
			console.error("Error fetching DAU users:", error);
			setDauUsers([]);
		} finally {
			setIsModalLoading(false);
		}
	};

	const fetchMauUsers = async () => {
		// Open modal immediately with loading state
		setMauUsers([]);
		setIsModalLoading(true);
		setIsMauModalOpen(true);
		
		try {
			const response = await fetch(
				`${process.env.REACT_APP_BASE_URL}/workspaces/mau-users`,
			);
			if (!response.ok) {
				throw new Error("Failed to fetch MAU users");
			}
			const data = await response.json();
			setMauUsers(data);
		} catch (error) {
			console.error("Error fetching MAU users:", error);
			setMauUsers([]);
		} finally {
			setIsModalLoading(false);
		}
	};

	const fetchUsersAtTime = async (timestamp) => {
		// Open modal immediately with loading state
		setTimeSpecificUsers([]);
		setSelectedTimePoint(timestamp);
		setIsModalLoading(true);
		setIsTimeSpecificModalOpen(true);
		
		try {
			const response = await fetch(
				`${process.env.REACT_APP_BASE_URL}/workspaces/users-at-time?timestamp=${encodeURIComponent(timestamp)}`,
			);
			if (!response.ok) {
				throw new Error("Failed to fetch users at specific time");
			}
			const data = await response.json();
			setTimeSpecificUsers(data);
		} catch (error) {
			console.error("Error fetching users at specific time:", error);
			setTimeSpecificUsers([]);
		} finally {
			setIsModalLoading(false);
		}
	};

	const fetchWorkspaceInfo = async () => {
		if (!workspaceId) return;
		try {
			const response = await fetch(
				`${process.env.REACT_APP_BASE_URL}/workspace/${workspaceId}`,
			);
			if (response.ok) {
				const data = await response.json();
				setWorkspaceInfo(data);
			}
		} catch (error) {
			console.error("Error fetching workspace info:", error);
		}
	};
	const generateMockHourlyData = () => {
		const now = new Date();
		const data = [];

		// Determine the number of data points based on the selected interval
		const pointsPerDay = timeInterval === "5min" ? 288 : // 24 * 12
			timeInterval === "15min" ? 96 : // 24 * 4
			timeInterval === "30min" ? 48 : // 24 * 2
			24; // 1 hour intervals

		const minutesToSubtract = timeInterval === "5min" ? 5 :
			timeInterval === "15min" ? 15 :
			timeInterval === "30min" ? 30 :
			60; // 1 hour

		for (let i = pointsPerDay - 1; i >= 0; i--) {
			const timestamp = new Date(now);
			timestamp.setMinutes(timestamp.getMinutes() - i * minutesToSubtract);

			// Generate realistic pattern with higher values during work hours
			const hourOfDay = timestamp.getHours();
			let baseValue = 20; // Base number of users

			// Increase during work hours (9am-6pm)
			if (hourOfDay >= 9 && hourOfDay <= 18) {
				baseValue = 60;
			}
			// Peak hours (11am-3pm)
			if (hourOfDay >= 11 && hourOfDay <= 15) {
				baseValue = 80;
			}
			// Lunch dip (12-1pm)
			if (hourOfDay === 12) {
				baseValue = 50;
			}

			// Add some randomness
			const activeUsers = Math.floor(
				baseValue + Math.random() * baseValue * 0.3,
			);

			data.push({
				hour: timestamp.toLocaleTimeString("en-US", {
					hour: "numeric",
					minute: "2-digit",
					hour12: true,
				}),
				timestamp: timestamp,
				activeUsers,
			});
		}

		return data;
	};
	// Generate realistic mock data for analytics
	const generateMockAnalyticsData = (timeRangeFilter = "1month") => {
		const now = new Date();
		const months = [];
		const days = [];

		// Generate last 6 months data
		for (let i = 5; i >= 0; i--) {
			const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
			months.push({
				month: date.toLocaleDateString("en-US", {
					month: "short",
					year: "numeric",
				}),
				mau: Math.floor(Math.random() * 500) + 1200 + (5 - i) * 50, // Growing trend
				interactions:
					Math.floor(Math.random() * 2000) + 3000 + (5 - i) * 100,
				dmMessages:
					Math.floor(Math.random() * 1500) + 2500 + (5 - i) * 80,
				stackMessages:
					Math.floor(Math.random() * 1000) + 1800 + (5 - i) * 60,
			});
		}

		// Generate data based on timeRangeFilter for performance
		let daysToGenerate;
		switch (timeRangeFilter) {
			case "1month":
				daysToGenerate = 30;
				break;
			case "3months":
				daysToGenerate = 90;
				break;
			case "6months":
				daysToGenerate = 180;
				break;
			default:
				daysToGenerate = 30; // Default fallback
		}
		for (let i = daysToGenerate - 1; i >= 0; i--) {
			const date = new Date(now);
			date.setDate(date.getDate() - i);
			const dayOfWeek = date.getDay();
			const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

			// Lower activity on weekends
			const baseDAU = isWeekend ? 150 : 300;
			const variation = Math.floor(Math.random() * 120) + baseDAU;

			// Create a gradual growth trend over the year
			const growthFactor = Math.floor((daysToGenerate - i) / 30) * 3; // Growth every month
			const seasonalVariation = Math.sin((i / 365) * 2 * Math.PI) * 20; // Seasonal pattern

			days.push({
				date: date.toLocaleDateString("en-US", {
					month: "short",
					day: "numeric",
				}),
				fullDate: date,
				dau: Math.max(50, variation + growthFactor + seasonalVariation), // Ensure minimum of 50 DAU
			});
		}

		return {
			monthlyData: months,
			dailyData: days,
			currentMetrics: {
				todayDAU: days[days.length - 1]?.dau || 0,
				yesterdayDAU: days[days.length - 2]?.dau || 0,
				currentMAU: months[months.length - 1]?.mau || 0,
				lastMonthMAU: months[months.length - 2]?.mau || 0,
				currentInteractions:
					months[months.length - 1]?.interactions || 0,
				lastMonthInteractions:
					months[months.length - 2]?.interactions || 0,
				currentDMs: months[months.length - 1]?.dmMessages || 0,
				lastMonthDMs: months[months.length - 2]?.dmMessages || 0,
			},
		};
	};

	// Calculate percentage change
	const calculatePercentageChange = (current, previous) => {
		if (previous === 0) return "+0%";
		const change = ((current - previous) / previous) * 100;
		const sign = change >= 0 ? "+" : "";
		return `${sign}${change.toFixed(1)}%`;
	};

	// Helper function to format numbers with commas and compact notation for large numbers
	const formatNumber = (num) => {
		if (num >= 1000000) {
			return (num / 1000000).toFixed(1) + "M";
		} else if (num >= 1000) {
			return (num / 1000).toFixed(1) + "K";
		}
		return new Intl.NumberFormat().format(num);
	};

	// Helper function for detailed views (no compact notation)
	const formatDetailedNumber = (num) => {
		return new Intl.NumberFormat().format(num);
	};

	// Utility functions for calculating inactive companies
	const calculateInactiveCompanies = {
		day1: (workspaceActivityData) => {
			if (workspaceActivityData.length === 0) return 0;
			const latestDay = workspaceActivityData[workspaceActivityData.length - 1];
			return latestDay.workspacesWithNoActivity || 0;
		},
		
		day2: (workspaceActivityData) => {
			if (workspaceActivityData.length < 2) return 0;
			const last2Days = workspaceActivityData.slice(-2);
			const totalWorkspaces = last2Days[0].totalWorkspaces;
			const minActiveWorkspaces = Math.min(...last2Days.map(day => day.workspacesWithActivity || 0));
			const maxConsistentlyInactive = totalWorkspaces - minActiveWorkspaces;
			return maxConsistentlyInactive;
		},
		
		day7: (workspaceActivityData) => {
			if (workspaceActivityData.length < 7) return 0;
			const last7Days = workspaceActivityData.slice(-7);
			const totalWorkspaces = last7Days[0].totalWorkspaces;
			const minActiveWorkspaces = Math.min(...last7Days.map(day => day.workspacesWithActivity || 0));
			const maxConsistentlyInactive = totalWorkspaces - minActiveWorkspaces;
			return maxConsistentlyInactive;
		}
	};


	// NOTE: fetchDailyInteractionData and fetchLeaderboardData are called by their 
	// respective filter-based useEffects below (lines ~1025 and ~1000), so we don't 
	// need to call them here. This prevents duplicate API calls on initial load.

	// Refetch leaderboard data when filter changes
	useEffect(() => {
		if (initialLoadComplete) {
			fetchLeaderboardData(leaderboardFilter);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [leaderboardFilter]);

	// **OPTIMIZED FILTER CHANGE HANDLER** - Immediate visual feedback
	const handleDailyInteractionsFilterChange = (newFilter) => {
		// **PREVENT RAPID FILTER CHANGES** - Don't change if already loading or same filter
		if (isDailyInteractionsLoading || newFilter === dailyInteractionsFilter) {
			console.log(`⏭️ Skipping filter change: loading=${isDailyInteractionsLoading}, same=${newFilter === dailyInteractionsFilter}`);
			return;
		}
		
		console.log(`🔄 Changing Daily Interactions filter from ${dailyInteractionsFilter} to ${newFilter}`);
		
		// **IMMEDIATE LOADING STATE** - No lag between click and visual feedback
		setIsDailyInteractionsLoading(true);
		
		// Update filter state
		setDailyInteractionsFilter(newFilter);
		
		// Data will be fetched by useEffect below
	};

	// Refetch daily interactions when filter changes
	useEffect(() => {
		if (initialLoadComplete) {
			fetchDailyInteractionData(dailyInteractionsFilter);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dailyInteractionsFilter]);

	// Transcripts filter change handler (mirrors interactions).
	const handleTranscriptsFilterChange = (newFilter) => {
		if (isDailyTranscriptsLoading || newFilter === transcriptsFilter) {
			return;
		}
		setIsDailyTranscriptsLoading(true);
		setTranscriptsFilter(newFilter);
		// Data fetched by the effect below.
	};

	// Fetch transcripts on initial load and whenever the filter changes.
	// (Unlike interactions, transcripts are not part of the analytics-batch,
	// so the initial fetch is driven here off initialLoadComplete.)
	useEffect(() => {
		if (initialLoadComplete) {
			fetchDailyTranscriptData(transcriptsFilter);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialLoadComplete, transcriptsFilter]);

	// Copilot filter change handler (mirrors transcripts).
	const handleCopilotFilterChange = (newFilter) => {
		if (isDailyCopilotLoading || newFilter === copilotFilter) {
			return;
		}
		setIsDailyCopilotLoading(true);
		setCopilotFilter(newFilter);
		// Data fetched by the effect below.
	};

	// Fetch copilot responses on initial load and whenever the filter changes.
	useEffect(() => {
		if (initialLoadComplete) {
			fetchDailyCopilotData(copilotFilter);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialLoadComplete, copilotFilter]);

	// Note: Initial workspace activity fetch is handled by fetchBatchAnalytics()

	// Fetch leaderboard data
	const fetchWorkspaceActivityData = async () => {
		try {
			setLoadingWorkspaceActivity(true);
			
			// Add performance timing
			const startTime = performance.now();
			
			// Add timeout to prevent hanging requests
			const controller = new AbortController();
			const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout
			
			const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5001/api';
			const response = await fetch(
				`${baseUrl}/workspaces/workspace-activity?days=30`,
				{
					signal: controller.signal,
					headers: {
						'Cache-Control': 'max-age=300' // 5 minutes cache
					}
				}
			);
			
			clearTimeout(timeoutId);
			
			if (!response.ok) {
				throw new Error(`Failed to fetch workspace activity: ${response.status}`);
			}
			
			const result = await response.json();
			
			// Performance measurement for monitoring
			const endTime = performance.now();
			// eslint-disable-next-line no-unused-vars
			const loadTime = endTime - startTime;
			
			if (result.success) {
				setWorkspaceActivityData(result.data || []);
			} else {
				console.error("Workspace activity API error:", result.error);
				setWorkspaceActivityData([]);
			}
		} catch (error) {
			if (error.name !== 'AbortError') {
				console.error("Error fetching workspace activity data:", error);
			}
			setWorkspaceActivityData([]);
		} finally {
			setLoadingWorkspaceActivity(false);
		}
	};

	// Generate mock user data for testing/fallback (similar to DAU/MAU modal format)
	const generateMockUserData = (activityType, count = 15) => {
		const mockUsers = [];
		const firstNames = ['John', 'Jane', 'Mike', 'Sarah', 'David', 'Emily', 'Chris', 'Lisa', 'Mark', 'Anna'];
		const lastNames = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Rodriguez', 'Martinez'];
		const workspaceNames = ['Tech Hub', 'Design Studio', 'Marketing Team', 'Dev Squad', 'Sales Force'];

		for (let i = 0; i < count; i++) {
			const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
			const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
			const workspaceName = workspaceNames[Math.floor(Math.random() * workspaceNames.length)];
			
			const user = {
				userId: `USR${1000 + i}`,
				name: `${firstName} ${lastName}`,
				email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@example.com`,
				role: Math.random() > 0.7 ? 'ADMIN' : 'MEMBER',
				workspace: `WS${Math.floor(Math.random() * 5) + 1}`,
				workspaceName: workspaceName,
				dmMessages: activityType === 'active' ? Math.floor(Math.random() * 50) : 0,
				stackMessages: activityType === 'active' ? Math.floor(Math.random() * 30) : 0,
				interactions: activityType === 'active' ? Math.floor(Math.random() * 100) : 0,
				lastActivity: activityType === 'active' 
					? new Date(Date.now() - Math.random() * 24 * 60 * 60 * 1000).toISOString()
					: null
			};
			mockUsers.push(user);
		}

		return mockUsers;
	};

	// Function to fetch workspace users for a specific date and activity type
	const fetchWorkspaceDetails = async (date, activityType) => {
		// Open modal immediately with loading state
		setSelectedWorkspaceDate(date);
		setWorkspaceModalType(activityType === 'active' ? 'WORKSPACE_ACTIVE' : 'WORKSPACE_INACTIVE');
		setWorkspaceUsers([]);
		setIsModalLoading(true);
		setIsWorkspaceModalOpen(true);
		
		try {
			// Fetch users from workspace-details endpoint
			const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5001/api';
			const response = await fetch(
				`${baseUrl}/workspaces/workspace-details?date=${date}&activityType=${activityType}`,
				{
					headers: {
						'Cache-Control': 'no-cache'
					}
				}
			);

			if (!response.ok) {
				throw new Error(`Failed to fetch workspace users: ${response.status}`);
			}

			const data = await response.json();
			
			if (data && data.length > 0) {
				console.log("Real workspace user data received:", data);
				
				// Debug: Verify workspace distribution counts
				const frontendWorkspaceDistribution = {};
				data.forEach(user => {
					const workspaceName = user.workspaceName || 'Unknown';
					if (!frontendWorkspaceDistribution[workspaceName]) {
						frontendWorkspaceDistribution[workspaceName] = 0;
					}
					frontendWorkspaceDistribution[workspaceName]++;
				});
				console.log('\n=== Frontend Workspace Distribution Verification ===');
				console.log(`Total users received: ${data.length}`);
				Object.entries(frontendWorkspaceDistribution).forEach(([workspace, count]) => {
					console.log(`${workspace}: ${count} users`);
				});
				console.log('====================================================\n');
				
				setWorkspaceUsers(data);
			} else {
				// Use mock data if API returns empty data or fails
				console.log("API returned empty data, using mock data for demonstration");
				const mockUsers = generateMockUserData(activityType, 15);
				setWorkspaceUsers(mockUsers);
			}
		} catch (error) {
			console.error("Error fetching workspace users:", error);
			
			// Fallback to mock data for testing/development
			const mockUsers = generateMockUserData(activityType, 10);
			console.log("Using mock workspace user data:", mockUsers);
			
			setWorkspaceUsers(mockUsers);
		} finally {
			setIsModalLoading(false);
		}
	};

	// Function to handle inactive companies click - shows workspace distribution without counts
	// eslint-disable-next-line no-unused-vars
	const handleInactiveCompaniesClick = async (days) => {
		// Open modal immediately with loading state
		const latestDate = workspaceActivityData.length > 0 
			? workspaceActivityData[workspaceActivityData.length - 1].date
			: new Date().toISOString().split('T')[0];
			
		setSelectedWorkspaceDate(latestDate);
		setWorkspaceModalType('INACTIVE_COMPANIES');
		setWorkspaceUsers([]);
		setIsModalLoading(true);
		setIsWorkspaceModalOpen(true);
		
		try {
			// Get all workspaces and their names for inactive companies display
			// We'll create a simplified data structure showing just workspace names
			const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5001/api';
			
			// Try to fetch real workspace data first
			const response = await fetch(
				`${baseUrl}/workspaces/workspace-details?date=${latestDate}&activityType=inactive`,
				{
					headers: {
						'Cache-Control': 'no-cache'
					}
				}
			);

			let workspaceData = [];
			if (response.ok) {
				const data = await response.json();
				// Group by workspace to get unique workspaces
				const workspaceGroups = {};
				data.forEach(user => {
					const wsName = user.workspaceName || 'Unknown Workspace';
					if (!workspaceGroups[wsName]) {
						workspaceGroups[wsName] = {
							workspaceName: wsName,
							workspace: user.workspace,
							name: `${wsName} (Inactive)`,
							email: '', // Hide email for workspace display
							role: 'WORKSPACE'
						};
					}
				});
				workspaceData = Object.values(workspaceGroups);
			}
			
			// If no real data, create mock inactive workspace list
			if (workspaceData.length === 0) {
				const inactiveWorkspaceNames = [
					'Dormant Corp', 'Quiet Industries', 'Silent Partners', 
					'Idle Enterprises', 'Paused Ventures', 'Resting Solutions',
					'Inactive Holdings', 'Stalled Projects', 'Hibernating Co.'
				];
				
				workspaceData = inactiveWorkspaceNames.slice(0, calculateInactiveCompanies.day1(workspaceActivityData)).map((name, index) => ({
					workspaceName: name,
					workspace: `WS${index + 100}`,
					name: `${name}`,
					email: '', // Hide email for workspace display
					role: 'WORKSPACE'
				}));
			}
			
			setWorkspaceUsers(workspaceData);
		} catch (error) {
			console.error("Error fetching inactive workspaces:", error);
			// Fallback to mock data
			const mockInactiveWorkspaces = ['Dormant Corp', 'Quiet Industries', 'Silent Partners'].map((name, index) => ({
				workspaceName: name,
				workspace: `WS${index + 100}`,
				name: `${name}`,
				email: '',
				role: 'WORKSPACE'
			}));
			setWorkspaceUsers(mockInactiveWorkspaces);
		} finally {
			setIsModalLoading(false);
		}
	};

	const fetchLeaderboardData = async (filter = leaderboardFilter) => {
		// Request deduplication - prevent duplicate calls while request is in flight
		const requestKey = `leaderboard-${filter}`;
		if (pendingRequests.current.has(requestKey)) {
			console.log(`⏭️ Skipping duplicate request: ${requestKey}`);
			return pendingRequests.current.get(requestKey);
		}
		
		try {
			setLoadingLeaderboard(true);
			const fetchPromise = fetch(
				`${process.env.REACT_APP_BASE_URL}/workspaces/leaderboard?filter=${filter}`
			);
			pendingRequests.current.set(requestKey, fetchPromise);
			
			const response = await fetchPromise;

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const data = await response.json();
			setLeaderboardData(data);
		} catch (error) {
			console.error("Error fetching leaderboard data:", error);
			// Only use mock data in development or if specifically requested
			if (process.env.NODE_ENV === 'development') {
				setLeaderboardData(generateMockLeaderboardData());
			} else {
				setLeaderboardData([]);
			}
		} finally {
			pendingRequests.current.delete(requestKey);
			setLoadingLeaderboard(false);
		}
	};

	// Generate mock leaderboard data
	const generateMockLeaderboardData = () => {
		const mockUsers = [
			{ name: "Sarah Johnson", email: "sarah.j@company.com", workspace: "Engineering", role: "Admin" },
			{ name: "Mike Chen", email: "mike.chen@startup.io", workspace: "Product", role: "Member" },
			{ name: "Emily Davis", email: "emily.d@corp.com", workspace: "Design", role: "Admin" },
			{ name: "Alex Rodriguez", email: "alex.r@tech.com", workspace: "Engineering", role: "Member" },
			{ name: "Jessica Wong", email: "jessica.w@company.com", workspace: "Marketing", role: "Admin" },
			{ name: "David Kim", email: "david.k@startup.io", workspace: "Sales", role: "Member" },
			{ name: "Rachel Green", email: "rachel.g@corp.com", workspace: "HR", role: "Member" },
			{ name: "Tom Wilson", email: "tom.w@tech.com", workspace: "Engineering", role: "Admin" },
			{ name: "Lisa Anderson", email: "lisa.a@company.com", workspace: "Finance", role: "Member" },
			{ name: "Chris Martinez", email: "chris.m@startup.io", workspace: "Operations", role: "Admin" },
			{ name: "Jennifer Lee", email: "jennifer.l@company.com", workspace: "Engineering", role: "Member" },
			{ name: "Robert Taylor", email: "robert.t@startup.io", workspace: "Product", role: "Admin" },
			{ name: "Amanda Smith", email: "amanda.s@corp.com", workspace: "Design", role: "Member" },
			{ name: "Kevin Brown", email: "kevin.b@tech.com", workspace: "Engineering", role: "Member" },
			{ name: "Nicole White", email: "nicole.w@company.com", workspace: "Marketing", role: "Member" },
			{ name: "Steven Garcia", email: "steven.g@startup.io", workspace: "Sales", role: "Admin" },
			{ name: "Michelle Clark", email: "michelle.c@corp.com", workspace: "HR", role: "Member" },
			{ name: "Daniel Lewis", email: "daniel.l@tech.com", workspace: "Engineering", role: "Member" },
			{ name: "Karen Miller", email: "karen.m@company.com", workspace: "Finance", role: "Admin" },
			{ name: "Brian Davis", email: "brian.d@startup.io", workspace: "Operations", role: "Member" },
			{ name: "Ashley Jones", email: "ashley.j@company.com", workspace: "Engineering", role: "Member" },
			{ name: "Mark Thompson", email: "mark.t@startup.io", workspace: "Product", role: "Member" },
			{ name: "Stephanie Moore", email: "stephanie.m@corp.com", workspace: "Design", role: "Admin" },
			{ name: "Jason Lee", email: "jason.l@tech.com", workspace: "Engineering", role: "Member" },
			{ name: "Heather Wilson", email: "heather.w@company.com", workspace: "Marketing", role: "Member" },
			{ name: "Ryan Hall", email: "ryan.h@startup.io", workspace: "Sales", role: "Member" },
			{ name: "Melissa Young", email: "melissa.y@corp.com", workspace: "HR", role: "Admin" },
			{ name: "James King", email: "james.k@tech.com", workspace: "Engineering", role: "Member" },
			{ name: "Lauren Scott", email: "lauren.s@company.com", workspace: "Finance", role: "Member" },
			{ name: "Andrew Adams", email: "andrew.a@startup.io", workspace: "Operations", role: "Member" }
		];

		return mockUsers.map((user, index) => ({
			rank: index + 1,
			userName: user.name,
			userEmail: user.email,
			workspaceName: user.workspace,
			userType: user.role,
			totalInteractions: Math.floor(Math.random() * 500) + 200,
			dmCount: Math.floor(Math.random() * 150) + 50,
			totalKnocks: Math.floor(Math.random() * 100) + 20,
			totalInvites: Math.floor(Math.random() * 80) + 10,
			sessionTime: Math.floor(Math.random() * 480) + 120, // minutes
			lastActive: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toLocaleDateString()
		}));
	};

	// Add this new function to fetch daily interaction data with filter support
	const fetchDailyInteractionData = async (timeFilter = dailyInteractionsFilter) => {
		// Request deduplication - prevent duplicate calls while request is in flight
		const requestKey = `daily-interactions-${timeFilter}`;
		if (pendingRequests.current.has(requestKey)) {
			console.log(`⏭️ Skipping duplicate request: ${requestKey}`);
			return pendingRequests.current.get(requestKey);
		}
		
		try {
			// **IMMEDIATE VISUAL FEEDBACK** - Set loading state right away
			setIsDailyInteractionsLoading(true);
			console.log(`🔍 Fetching daily interactions with filter: ${timeFilter}`);
			
			const fetchPromise = fetch(`${process.env.REACT_APP_BASE_URL}/workspaces/daily-interactions?timeRange=${timeFilter}`);
			pendingRequests.current.set(requestKey, fetchPromise);
			
			const response = await fetchPromise;

			if (!response.ok) {
				throw new Error(`Failed to fetch daily interaction data: ${response.status} ${response.statusText}`);
			}

			const data = await response.json();
			console.log(`✅ Daily interactions loaded: ${data.length} days of data for ${timeFilter}`);
			setDailyInteractionData(data);
		} catch (error) {
			console.error("Error fetching daily interaction data:", error);
			// Generate mock data if API fails with the specified filter
			const mockData = generateMockDailyInteractionData(timeFilter);
			console.log(`📊 Using mock data for ${timeFilter}: ${mockData.length} days`);
			setDailyInteractionData(mockData);
		} finally {
			pendingRequests.current.delete(requestKey);
			setIsDailyInteractionsLoading(false);
		}
	};

	// Add mock data generator for daily interactions with filter support
	const generateMockDailyInteractionData = (timeFilter = "current-month") => {
		const data = [];
		const now = new Date();
		let daysToGenerate = 30; // Default for fallback

		// Calculate date range based on filter
		let startDate = new Date();
		let endDate = new Date();

		switch (timeFilter) {
			case "all-time":
				// Start from May 1st of current year
				startDate = new Date(now.getFullYear(), 4, 1); // Month 4 = May (0-indexed)
				endDate = new Date();
				daysToGenerate = Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
				break;
			case "current-month":
				// Generate current month data
				startDate = new Date(now.getFullYear(), now.getMonth(), 1);
				endDate = new Date();
				daysToGenerate = Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
				break;
			case "last-month":
				// Generate last month data
				startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
				endDate = new Date(now.getFullYear(), now.getMonth(), 0); // Last day of previous month
				daysToGenerate = Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
				break;
			default:
				// Default to 30 days
				startDate.setDate(startDate.getDate() - 30);
				endDate = new Date();
				daysToGenerate = 30;
		}

		console.log(`📊 Generating ${daysToGenerate} days of mock data for ${timeFilter} (${startDate.toDateString()} to ${endDate.toDateString()})`);

		for (let i = 0; i < daysToGenerate; i++) {
			const date = new Date(startDate);
			date.setDate(startDate.getDate() + i);

			// Generate realistic patterns with some randomness
			const baseValue = 20 + Math.random() * 10;
			const dayOfWeek = date.getDay();
			const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
			const factor = isWeekend ? 0.6 : 1;

			// Add some growth trend for all-time data
			const growthFactor = timeFilter === "all-time" ? (1 + (i / daysToGenerate) * 0.3) : 1;

			data.push({
				date: date.toLocaleDateString("en-US", {
					month: "short",
					day: "numeric",
				}),
				fullDate: date,
				dms: Math.floor(
					baseValue * factor * growthFactor * (1.5 + Math.random() * 0.5),
				),
				knocks: Math.floor(
					baseValue * factor * growthFactor * (0.7 + Math.random() * 0.3),
				),
				invites: Math.floor(
					baseValue * factor * growthFactor * (0.5 + Math.random() * 0.3),
				),
			});
		}

		return data;
	};

	// Fetch daily transcript counts (mirrors fetchDailyInteractionData).
	const fetchDailyTranscriptData = async (timeFilter = transcriptsFilter) => {
		const requestKey = `daily-transcripts-${timeFilter}`;
		if (pendingRequests.current.has(requestKey)) {
			console.log(`⏭️ Skipping duplicate request: ${requestKey}`);
			return pendingRequests.current.get(requestKey);
		}

		try {
			setIsDailyTranscriptsLoading(true);
			console.log(`🔍 Fetching daily transcripts with filter: ${timeFilter}`);

			const fetchPromise = fetch(`${process.env.REACT_APP_BASE_URL}/workspaces/daily-transcripts?timeRange=${timeFilter}`);
			pendingRequests.current.set(requestKey, fetchPromise);

			const response = await fetchPromise;
			if (!response.ok) {
				throw new Error(`Failed to fetch daily transcript data: ${response.status} ${response.statusText}`);
			}

			const data = await response.json();
			console.log(`✅ Daily transcripts loaded: ${data.length} days of data for ${timeFilter}`);
			setDailyTranscriptData(data);
		} catch (error) {
			console.error("Error fetching daily transcript data:", error);
			const mockData = generateMockDailyTranscriptData(timeFilter);
			console.log(`📊 Using mock transcript data for ${timeFilter}: ${mockData.length} days`);
			setDailyTranscriptData(mockData);
		} finally {
			pendingRequests.current.delete(requestKey);
			setIsDailyTranscriptsLoading(false);
		}
	};

	// Mock fallback for the transcripts chart (single `transcripts` series).
	// Used ONLY if the API call fails. Duration and words are hard, real
	// quantities — we never invent them, so they stay 0 in the fallback rather
	// than showing fabricated minutes/words.
	const generateMockDailyTranscriptData = (timeFilter = "current-month") => {
		return generateMockDailyInteractionData(timeFilter).map((row) => ({
			date: row.date,
			fullDate: row.fullDate,
			transcripts: Math.floor((row.knocks || 0) * 0.4 + (row.invites || 0) * 0.3),
			durationMinutes: 0,
			words: 0,
		}));
	};

	const fetchDailyCopilotData = async (timeFilter = copilotFilter) => {
		const requestKey = `daily-copilot-responses-${timeFilter}`;
		if (pendingRequests.current.has(requestKey)) {
			console.log(`⏭️ Skipping duplicate request: ${requestKey}`);
			return pendingRequests.current.get(requestKey);
		}

		try {
			setIsDailyCopilotLoading(true);
			console.log(`🔍 Fetching daily copilot responses with filter: ${timeFilter}`);

			const fetchPromise = fetch(`${process.env.REACT_APP_BASE_URL}/workspaces/daily-copilot-responses?timeRange=${timeFilter}`);
			pendingRequests.current.set(requestKey, fetchPromise);

			const response = await fetchPromise;
			if (!response.ok) {
				throw new Error(`Failed to fetch daily copilot responses: ${response.status} ${response.statusText}`);
			}

			const data = await response.json();
			console.log(`✅ Daily copilot responses loaded: ${data.length} days of data for ${timeFilter}`);
			setDailyCopilotData(data);
		} catch (error) {
			console.error("Error fetching daily copilot response data:", error);
			const mockData = generateMockDailyCopilotData(timeFilter);
			console.log(`📊 Using mock copilot data for ${timeFilter}: ${mockData.length} days`);
			setDailyCopilotData(mockData);
		} finally {
			pendingRequests.current.delete(requestKey);
			setIsDailyCopilotLoading(false);
		}
	};

	// Mock fallback for the copilot chart (single `copilotResponses` series).
	const generateMockDailyCopilotData = (timeFilter = "current-month") => {
		return generateMockDailyInteractionData(timeFilter).map((row) => ({
			date: row.date,
			fullDate: row.fullDate,
			copilotResponses: Math.floor((row.knocks || 0) * 0.2),
		}));
	};

	// Format a minute count as "Xh Ym" (or "Ym" under an hour) for display.
	const formatMinutes = (mins) => {
		const m = Math.round(mins || 0);
		if (m < 60) return `${m}m`;
		return `${Math.floor(m / 60)}h ${m % 60}m`;
	};

	// Leaderboard Table Component
	// eslint-disable-next-line no-unused-vars
	const LeaderboardTable = () => {
		// eslint-disable-next-line no-unused-vars
		const formatTime = (minutes) => {
			const hours = Math.floor(minutes / 60);
			const mins = minutes % 60;
			return `${hours}h ${mins}m`;
		};

		const getRankBadge = (rank) => {
			if (rank === 1) return "🥇";
			if (rank === 2) return "🥈";
			if (rank === 3) return "🥉";
			return `#${rank}`;
		};

		if (loadingLeaderboard) {
			return (
				<div className="leaderboard-card">
					<div className="leaderboard-header">
						<h3>🏆 Leaderboard</h3>
					</div>
					<div className="loading">Loading leaderboard...</div>
				</div>
			);
		}

		return (
			<div className="leaderboard-card">
				<div className="leaderboard-header">
					<div className="leaderboard-header-content">
						<div className="leaderboard-header-left">
							<h3>🏆 Leaderboard</h3>
							<div className="leaderboard-description">Ranked by Activity Score: Interactions + DMs + (Knocks × 2) + (Invites × 3)</div>
						</div>
						<div className="leaderboard-header-right">
							<div className="leaderboard-filter">
								<select 
									value={leaderboardFilter} 
									onChange={(e) => setLeaderboardFilter(e.target.value)}
									className="filter-dropdown"
								>
									<option value="all-time">All Time</option>
									<option value="current-month">Current Month</option>
									<option value="last-month">Last Month</option>
								</select>
							</div>
						</div>
					</div>
				</div>
				<div className="leaderboard-content">
					{!leaderboardData || leaderboardData.length === 0 ? (
						<div className="no-data-message">
							<div className="no-data-icon">📊</div>
							<h4>No User Data Available</h4>
							<p>There are currently no active users with sufficient activity data to display in the leaderboard.</p>
							<p className="hint">Users need to have interactions, messages, or session time to appear here.</p>
						</div>
					) : (
						<div className="table-wrapper">
							<table className="leaderboard-table">
								<thead>
									<tr>
										<th>Rank</th>
										<th>User</th>
										<th>Activity Score</th>
										<th>Workspace</th>
										<th>Role</th>
										<th>DMs</th>
										<th>Knocks</th>
										<th>Invites</th>
									</tr>
								</thead>
								<tbody>
									{leaderboardData.map((user, index) => (
										<tr key={`${user.userEmail}-${index}`} className={`rank-${user.rank}`}>
											<td className="rank-cell">
												<span className="rank-badge">
													{getRankBadge(user.rank)}
												</span>
											</td>
																						<td className="user-cell">
												<div className="user-info">
													<div className="user-name">
														{user.rank === 1 && "👑 "}
														{user.rank === 2 && "🥈 "}
														{user.rank === 3 && "🥉 "}
														{user.userName}
													</div>
													<div className="user-email">{user.userEmail}</div>
												</div>
											</td>
											<td className="activity-score-cell">
												<span className="activity-score-value">
													{user.activityScore || (user.totalInteractions + user.dmCount + (user.totalKnocks * 2) + (user.totalInvites * 3))}
												</span>
											</td>
											<td className="workspace-cell">
												<span className="workspace-name">{user.workspaceName}</span>
											</td>
											<td className="role-cell">
												<span className={`role-badge ${user.userType?.toLowerCase()}`}>
													{user.userType}
												</span>
											</td>
											<td className="dm-cell">
												<span className="metric-value">{user.dmCount}</span>
											</td>
										<td className="knocks-cell">
											<span className="metric-value">{user.totalKnocks}</span>
										</td>
											<td className="invites-cell">
												<span className="metric-value">{user.totalInvites}</span>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</div>
			</div>
		);
	};

	const DailyInteractionChart = ({
		data,
		height = 300,
		// Metrics + row-processing are parameterized so this same chart renders
		// both the interactions view (DMs / knocks+invites) and the transcripts
		// view (single transcripts line). Defaults preserve the original behavior.
		metrics = [
			{ key: "dms", label: "DMs", color: "#4299E1" },
			{ key: "knocksAndInvites", label: "Knocks + Invites", color: "#48BB78" },
		],
		processRow = (item) => ({
			...item,
			knocksAndInvites: (item.knocks || 0) + (item.invites || 0),
		}),
		// Optional: (hoveredData) => [{ label, value }] extra read-only rows shown
		// in the hover tooltip (e.g. per-day duration/words that aren't plotted).
		extraTooltipRows = null,
	}) => {
		const [hoveredData, setHoveredData] = useState(null);
		const [forceUpdate, setForceUpdate] = useState(0);
		const chartRef = useRef(null);

		// Force re-render when container is mounted and has dimensions
		useEffect(() => {
			if (chartRef.current && chartRef.current.clientWidth > 0) {
				// Small delay to ensure layout is complete, then force update once
				const timeout = setTimeout(() => {
					setForceUpdate(1);
				}, 100);
				
				return () => clearTimeout(timeout);
			}
		}, [data]);

		// Add resize observer to handle window resize
		useEffect(() => {
			if (!chartRef.current) return;

			const resizeObserver = new ResizeObserver(() => {
				// Only update if we have a valid container width
				if (chartRef.current && chartRef.current.clientWidth > 0) {
					setTimeout(() => {
						setForceUpdate(prev => prev + 1);
					}, 100);
				}
			});

			resizeObserver.observe(chartRef.current);

			return () => {
				resizeObserver.disconnect();
			};
		}, []);

		// Process each row via the (parameterized) processRow function.
		const processedData = useMemo(() => {
			if (!data || data.length === 0) return [];
			return data.map(processRow);
		// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [data]);

		// Find max value across all metrics for y-axis scale
		const maxValue = useMemo(() => {
			if (!processedData || processedData.length === 0) return 100;
			const raw = Math.max(
				...processedData.map((item) =>
					Math.max(...metrics.map((m) => item[m.key] || 0)),
				),
			);
			return (raw > 0 ? raw : 100) * 1.1; // Add 10% padding to the max value
		// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [processedData]);

		const createYAxisLabels = (min, max) => {
			const step = Math.ceil(max / 5);
			const labels = [];
			for (let i = 0; i <= 5; i++) {
				labels.push(i * step);
			}
			return labels;
		};

		const yLabels = createYAxisLabels(0, maxValue);

		// Modify the path creation functions to ensure points near the edge are visible
		const createSmoothPath = (data, metricKey) => {
			if (!data || data.length === 0 || !chartRef.current) return "";

			const chartWidth = chartRef.current.clientWidth - 60; // Account for y-axis width
			const chartHeight = height - 60; // Account for x-axis height

			// Adjust the xStep to ensure the last points are visible within the chart boundary
			const xStep = (chartWidth - 80) / (data.length - 1); // Further increase padding to keep points inside
			const yScale = chartHeight / maxValue;

			let path = `M 30,${chartHeight - (data[0][metricKey] || 0) * yScale}`;

			for (let i = 1; i < data.length; i++) {
				const x = 30 + i * xStep;
				const y = chartHeight - (data[i][metricKey] || 0) * yScale;
				const prevX = 30 + (i - 1) * xStep;
				const prevY =
					chartHeight - (data[i - 1][metricKey] || 0) * yScale;

				const cpx1 = prevX + xStep / 3;
				const cpy1 = prevY;
				const cpx2 = x - xStep / 3;
				const cpy2 = y;

				path += ` C ${cpx1},${cpy1} ${cpx2},${cpy2} ${x},${y}`;
			}

			return path;
		};

		const createFillPath = (data, metricKey) => {
			if (!data || data.length === 0 || !chartRef.current) return "";

			const chartWidth = chartRef.current.clientWidth - 60;
			const chartHeight = height - 60;

			// Adjust the xStep to ensure the last points are visible within the chart boundary
			const xStep = (chartWidth - 80) / (data.length - 1); // Further increase padding to keep points inside
			const yScale = chartHeight / maxValue;

			let path = `M 30,${chartHeight - (data[0][metricKey] || 0) * yScale}`;

			for (let i = 1; i < data.length; i++) {
				const x = 30 + i * xStep;
				const y = chartHeight - (data[i][metricKey] || 0) * yScale;
				const prevX = 30 + (i - 1) * xStep;
				const prevY =
					chartHeight - (data[i - 1][metricKey] || 0) * yScale;

				const cpx1 = prevX + xStep / 3;
				const cpy1 = prevY;
				const cpx2 = x - xStep / 3;
				const cpy2 = y;

				path += ` C ${cpx1},${cpy1} ${cpx2},${cpy2} ${x},${y}`;
			}

			const lastX = 30 + (data.length - 1) * xStep; // Ensure the last X stays within bounds

			return `${path} L ${lastX},${chartHeight} L 30,${chartHeight} Z`;
		};

		const handleMouseMove = (event) => {
			if (!chartRef.current || !processedData || processedData.length === 0) return;

			const chartRect = chartRef.current.getBoundingClientRect();
			const chartWidth = chartRect.width - 60; // Account for y-axis
			const mouseX = event.clientX - chartRect.left - 30; // Adjust for y-axis offset

			// Adjust the xStep to match the path creation functions
			const xStep = (chartWidth - 80) / (processedData.length - 1);
			const index = Math.min(
				Math.max(Math.round(mouseX / xStep), 0),
				processedData.length - 1,
			);

			if (index >= 0 && index < processedData.length) {
				setHoveredData({
					...processedData[index],
					x: 30 + index * xStep, // Add y-axis offset
					index: index,
					mouseX: event.clientX - chartRect.left,
					mouseY: event.clientY - chartRect.top,
				});
			}
		};

		const handleMouseLeave = () => {
			setHoveredData(null);
		};

		// Calculate data point positions for all metrics - adjusted to match path creation
		const dataPoints = useMemo(() => {
			if (!processedData || processedData.length === 0 || !chartRef.current || chartRef.current.clientWidth <= 0) return [];

			const chartWidth = chartRef.current.clientWidth - 60;
			const chartHeight = height - 60;
			// Adjust the xStep to ensure the last points are visible within the chart boundary
			const xStep = (chartWidth - 80) / (processedData.length - 1);
			const yScale = chartHeight / maxValue;

			return processedData.map((item, index) => {
				const x = 30 + index * xStep;

				return {
					index,
					x,
					date: item.date,
					points: metrics.map((metric) => ({
						key: metric.key,
						color: metric.color,
						x,
						y: chartHeight - (item[metric.key] || 0) * yScale,
						value: item[metric.key] || 0,
					})),
				};
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [processedData, maxValue, metrics, height, forceUpdate]);

		// Function to determine tooltip position to keep it inside chart
		// eslint-disable-next-line no-unused-vars
		const getTooltipPosition = (index, totalPoints, mouseX, chartWidth) => {
			// Calculate the horizontal position percentage
			const positionPercentage = mouseX / chartWidth;

			// If we're in the right 30% of the chart, position tooltip to the left of the cursor
			if (positionPercentage > 0.7) {
				return "left";
			}

			// If we're in the left 30% of the chart, position tooltip to the right of the cursor
			if (positionPercentage < 0.3) {
				return "right";
			}

			// For points in the middle, position based on which half they're in
			return positionPercentage > 0.5 ? "left" : "right";
		};

		// Completely revised getXAxisLabels function to show only key dates
		const getXAxisLabels = () => {
			if (!processedData || processedData.length === 0) return [];

			const totalPoints = processedData.length;

			// On mobile show fewer labels to prevent overcrowding
			const isMobile = window.innerWidth < 768;
			const middleLabelsCount = isMobile ? 1 : 3;

			// Always include first date
			const result = [{ date: processedData[0].date, index: 0 }];

			const step = Math.floor(
				(totalPoints - 2) / (middleLabelsCount + 1),
			);

			for (let i = 1; i <= middleLabelsCount; i++) {
				const index = i * step;
				if (index > 0 && index < totalPoints - 1) {
					result.push({
						date: processedData[index].date,
						index,
					});
				}
			}

			// Always include last date (today)
			result.push({
				date: processedData[totalPoints - 1].date,
				index: totalPoints - 1,
			});

			return result;
		};

		// Early return after all hooks have been called
		if (!processedData || processedData.length === 0) {
			return <div className="loading">Loading chart data...</div>;
		}

		// Show loading while container is not ready to prevent incorrect initial render
		if (!chartRef.current || chartRef.current.clientWidth <= 0) {
			return (
				<div 
					className="chart-container daily-interaction-chart"
					ref={chartRef}
					style={{
						position: "relative",
						width: "100%",
						height: `${height + 40}px`,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<div className="loading">Loading chart...</div>
				</div>
			);
		}

		const xAxisLabels = getXAxisLabels();

		return (
			<div
				className="chart-container daily-interaction-chart"
				ref={chartRef}
				style={{
					position: "relative",
					width: "100%",
					height: `${height + 40}px`,
					overflow: "hidden",
					paddingBottom: "20px",
				}}
			>
				<svg
					width="100%"
					height={height}
					onMouseMove={handleMouseMove}
					onMouseLeave={handleMouseLeave}
					style={{ overflow: "visible" }}
				>
					{/* Grid lines */}
					{yLabels.map((label, i) => (
						<line
							key={`grid-${i}`}
							x1="30"
							y1={
								height - 60 - (label / maxValue) * (height - 60)
							}
							x2="100%"
							y2={
								height - 60 - (label / maxValue) * (height - 60)
							}
							stroke="#e2e8f0"
							strokeWidth="1"
							strokeOpacity="0.3"
						/>
					))}

					{/* Fill paths */}
					{metrics.map((metric) => (
						<path
							key={`fill-${metric.key}`}
							d={createFillPath(processedData, metric.key)}
							fill={metric.color}
							opacity="0.1"
						/>
					))}

					{/* Line paths */}
					{metrics.map((metric) => (
						<path
							key={`line-${metric.key}`}
							d={createSmoothPath(processedData, metric.key)}
							fill="none"
							stroke={metric.color}
							strokeWidth="2"
						/>
					))}

					{/* Data points with increased opacity for better visibility */}
					{dataPoints.map((dayData) =>
						dayData.points.map((point) => {
							// Highlight the last day's points
							const isLastDay = dayData.index === processedData.length - 1;
							return (
								<circle
									key={`point-${dayData.index}-${point.key}`}
									cx={point.x}
									cy={point.y}
									r={isLastDay ? "5" : "4"} // Make last day's points larger
									fill={point.color}
									opacity={isLastDay ? "1" : "0.8"} // Make last day's points fully opaque
									stroke="#fff"
									strokeWidth={isLastDay ? "2" : "1.5"} // Thicker stroke for last day's points
								/>
							);
						}),
					)}

					{/* Hover indicator */}
					{hoveredData && (
						<>
							<line
								x1={hoveredData.x}
								y1="0"
								x2={hoveredData.x}
								y2={height - 60}
								stroke="#718096"
								strokeWidth="1"
								strokeDasharray="4,4"
							/>
							{metrics.map((metric) => {
								const pointY =
									height -
									60 -
									(hoveredData[metric.key] || 0) *
										((height - 60) / maxValue);
								return (
									<circle
										key={`hover-point-${metric.key}`}
										cx={hoveredData.x}
										cy={pointY}
										r="6" // Increased hover point size
										fill={metric.color}
										stroke="#fff"
										strokeWidth="2"
									/>
								);
							})}
						</>
					)}
				</svg>

				{/* Y-axis labels */}
				<div
					className="y-axis-labels daily-interaction-labels"
					style={{
						position: "absolute",
						left: 0,
						top: 0,
						height: `${height - 60}px`,
						width: "30px",
						display: "flex",
						flexDirection: "column",
						justifyContent: "space-between",
						color: "#718096",
						fontSize: "10px",
						paddingTop: "5px",
						paddingBottom: "5px",
					}}
				>
					{yLabels.map((label, i) => (
						<span
							key={`y-label-${i}`}
							style={{
								position: "absolute",
								left: 0,
								bottom: `${(label / maxValue) * 100}%`,
								transform: "translateY(50%)",
								width: "100%",
								textAlign: "right",
								paddingRight: "5px",
							}}
						>
							{label}
						</span>
					))}
				</div>

				{/* X-axis labels with improved positioning */}
				<div
					style={{
						position: "absolute",
						bottom: 0,
						left: "30px",
						width: "calc(100% - 30px)",
						height: "40px",
						display: "flex",
					}}
				>
					{xAxisLabels.map((item) => {
						// Calculate position based on chart width and leave more space at edges
						const chartWidth =
							chartRef.current?.clientWidth - 60 || 800;
						const xStep = (chartWidth - 80) / (processedData.length - 1);
						const xPos = 30 + item.index * xStep;

						// Highlight today's date (last date)
						const isLastDate = item.index === processedData.length - 1;

						// Special positioning for the last date to ensure it's visible
						const labelStyle = isLastDate
							? {
									position: "absolute",
									right: "10px", // Fixed position from right edge
									bottom: "15px",
									transform: "none", // Don't use translateX to avoid clipping
									textAlign: "right",
								}
							: {
									position: "absolute",
									left: `${xPos}px`,
									bottom: "15px",
									transform: "translateX(-50%)",
									textAlign: "center",
								};

						return (
							<div
								key={`x-label-${item.index}`}
								style={labelStyle}
							>
								<div
									style={{
										transform: isLastDate
											? "none"
											: "rotate(-45deg)",
										transformOrigin: "top left",
										fontSize: window.innerWidth < 768
											? (isLastDate ? "10px" : "9px")
											: (isLastDate ? "12px" : "11px"),
										fontWeight: isLastDate ? "700" : "500",
										color: "#ffffff",
										whiteSpace: "nowrap",
										paddingTop: "5px",
										backgroundColor: isLastDate
											? "rgba(0,0,0,0.5)"
											: "transparent",
										padding: isLastDate ? "3px 6px" : "0",
										borderRadius: isLastDate ? "4px" : "0",
									}}
								>
									{item.date}
								</div>
							</div>
						);
					})}
				</div>

				{/* Tooltip with improved positioning */}
				{/* Tooltip with improved positioning */}
				{hoveredData && (
					<div
						className="chart-tooltip"
						style={{
							position: "absolute",
							left: `${hoveredData.x}px`,
							top: Math.max(10, hoveredData.mouseY - 160), // Keep tooltip within chart top boundary
							padding: "10px 14px", // Larger padding
							borderRadius: "8px", // Rounder corners
							fontSize: "12px",
							zIndex: 1000,
							maxWidth: "180px", // Wider tooltip
							transform: "translateX(-50%)", // Center horizontally over the point
							pointerEvents: "none", // Ensure it doesn't interfere with mouse events
						}}
					>
						<div
							className="chart-tooltip-date"
							style={{
								fontWeight: "bold",
								marginBottom: "8px",
								fontSize: "14px", // Larger date text
								paddingBottom: "4px",
							}}
						>
							{hoveredData.date}
						</div>
						{metrics.map((metric) => (
							<div
								key={`tooltip-${metric.key}`}
								style={{
									marginTop: "6px",
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
								}}
							>
								<div
									style={{
										display: "flex",
										alignItems: "center",
									}}
								>
									<span
										style={{
											color: metric.color,
											marginRight: "5px",
											fontSize: "16px", // Larger bullet point
										}}
									>
										●
									</span>
									<span>{metric.label}:</span>
								</div>
								<strong style={{ marginLeft: "10px" }}>
									{hoveredData[metric.key] || 0}
								</strong>
							</div>
						))}
						{typeof extraTooltipRows === "function" &&
							extraTooltipRows(hoveredData).map((row) => (
								<div
									key={`tooltip-extra-${row.label}`}
									style={{
										marginTop: "6px",
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
										opacity: 0.9,
									}}
								>
									<span>{row.label}:</span>
									<strong style={{ marginLeft: "10px" }}>
										{row.value}
									</strong>
								</div>
							))}
						<div
							style={{
								position: "absolute",
								bottom: "-8px",
								left: "50%",
								transform: "translateX(-50%)",
								width: 0,
								height: 0,
								borderLeft: "8px solid transparent",
								borderRight: "8px solid transparent",
								borderTop: "8px solid rgba(0, 0, 0, 0.85)",
							}}
						></div>
					</div>
				)}

				{/* Chart legend */}
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						gap: "20px",
						position: "absolute",
						bottom: "0",
						width: "100%",
						textAlign: "center",
					}}
				>
					{metrics.map((metric) => (
						<div
							key={`legend-${metric.key}`}
							style={{
								display: "flex",
								alignItems: "center",
								gap: "5px",
							}}
						>
							<div
								style={{
									width: "12px",
									height: "12px",
									borderRadius: "2px",
									backgroundColor: metric.color,
								}}
							></div>
							<span>{metric.label}</span>
						</div>
					))}
				</div>
			</div>
		);
	};
	// ... existing code ...
	// Add this component to display the hourly active users chart
	const HourlyActiveUsersChart = ({
		data,
		height = 300,
		color = "#4299e1",
		onPointClick,
	}) => {
		const [hoveredPoint, setHoveredPoint] = useState(null);
		const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

		if (!data || data.length === 0) return <div>No data available</div>;

		// Ensure all data items have required properties and filter out invalid ones
		const validData = data.filter((item) => 
			item && 
			typeof item.activeUsers === 'number' && 
			item.timestamp
		);

		if (validData.length === 0) {
			return <div>No valid data available for the selected time interval</div>;
		}

		const maxValue = Math.max(...validData.map((d) => d.activeUsers));
		const minValue = 0; // Start from 0 for better visualization
		const range = maxValue - minValue || 1;
		const timeRanges = {
			"5min": { label: "5 Minutes", duration: 24, minutes: 5 },
			"15min": { label: "15 Minutes", duration: 24, minutes: 15 }, // Show last 24 hours
			"30min": { label: "30 Minutes", duration: 24, minutes: 30 }, // Show last 24 hours
			"1hour": { label: "1 Hour", duration: 24, minutes: 60 }, // Show last 24 hours
		};

		// Get the interval minutes from the configuration
		// eslint-disable-next-line no-unused-vars
		const intervalMinutes = timeRanges[timeInterval].minutes;

		// All intervals now show 24 hours - use the already validated data
		const filteredData = validData;

		// Add a check for empty filtered data
		if (!filteredData || filteredData.length === 0) {
			return <div>No data available for selected time interval</div>;
		}



		// Create better y-axis labels with nice round numbers
		const createYAxisLabels = (min, max) => {
			const range = max - min;
			const step = Math.ceil(range / 4); // Create 4-5 labels
			const roundedMin = Math.floor(min / step) * step;
			const roundedMax = Math.ceil(max / step) * step;

			const labels = [];
			for (let i = roundedMax; i >= roundedMin; i -= step) {
				if (i >= min && i <= max) {
					labels.push(i);
				}
			}

			// Ensure we have at least min and max
			if (!labels.includes(max)) labels.unshift(max);
			if (!labels.includes(min)) labels.push(min);

			return labels.sort((a, b) => b - a); // Sort descending for top to bottom
		};

		const yLabels = createYAxisLabels(minValue, maxValue);

		// Create smooth curve using cubic bezier
		const createSmoothPath = (data) => {
			let path = "";
			for (let i = 0; i < data.length; i++) {
				const x = (i / (data.length - 1)) * 100;
				const y =
					100 - ((data[i].activeUsers - minValue) / range) * 100;

				if (i === 0) {
					path += `M ${x} ${y}`;
				} else {
					// Calculate control points for smooth curve
					const prevX = ((i - 1) / (data.length - 1)) * 100;
					const prevY =
						100 -
						((data[i - 1].activeUsers - minValue) / range) * 100;

					const cp1x = prevX + (x - prevX) * 0.3;
					const cp1y = prevY;
					const cp2x = x - (x - prevX) * 0.3;
					const cp2y = y;

					path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${x} ${y}`;
				}
			}
			return path;
		};

		const pathData = createSmoothPath(data);

		// Create filled area path
		const createFillPath = (data) => {
			let path = "";
			// Start from bottom left
			path += `M 0 100`;

			// Add the smooth curve
			for (let i = 0; i < data.length; i++) {
				const x = (i / (data.length - 1)) * 100;
				const y =
					100 - ((data[i].activeUsers - minValue) / range) * 100;

				if (i === 0) {
					path += ` L ${x} ${y}`;
				} else {
					// Calculate control points for smooth curve
					const prevX = ((i - 1) / (data.length - 1)) * 100;
					const prevY =
						100 -
						((data[i - 1].activeUsers - minValue) / range) * 100;

					const cp1x = prevX + (x - prevX) * 0.3;
					const cp1y = prevY;
					const cp2x = x - (x - prevX) * 0.3;
					const cp2y = y;

					path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${x} ${y}`;
				}
			}

			// Close the path by going to bottom right and back to start
			path += ` L 100 100 Z`;
			return path;
		};

		const fillPath = createFillPath(data);

		// Handle mouse movement over the chart
		const handleMouseMove = (event) => {
			const rect = event.currentTarget.getBoundingClientRect();
			const x = ((event.clientX - rect.left) / rect.width) * 100;
			// const y = ((event.clientY - rect.top) / rect.height) * 100;

			// Find the closest data point
			const dataIndex = Math.round((x / 100) * (data.length - 1));
			const clampedIndex = Math.max(
				0,
				Math.min(data.length - 1, dataIndex),
			);

			setHoveredPoint(clampedIndex);
			setMousePosition({
				x: event.clientX - rect.left,
				y: event.clientY - rect.top,
			});
		};

		const handleMouseLeave = () => {
			setHoveredPoint(null);
		};

		const handleChartClick = (event) => {
			if (!onPointClick) return;
			
			const rect = event.currentTarget.getBoundingClientRect();
			const x = ((event.clientX - rect.left) / rect.width) * 100;

			// Find the closest data point
			const dataIndex = Math.round((x / 100) * (data.length - 1));
			const clampedIndex = Math.max(
				0,
				Math.min(data.length - 1, dataIndex),
			);

			const clickedDataPoint = data[clampedIndex];
			if (clickedDataPoint && clickedDataPoint.timestamp) {
				onPointClick(clickedDataPoint.timestamp, clickedDataPoint);
			}
		};

		return (
			<div className="relative">
				<div
					className="chart-container hourly-chart"
					style={{ height: height + 40, position: "relative" }}
				>
					<svg
						width="100%"
						height={height}
						viewBox="0 0 100 100"
						preserveAspectRatio="none"
						style={{ overflow: "hidden", cursor: onPointClick ? "pointer" : "default" }}
						onMouseMove={handleMouseMove}
						onMouseLeave={handleMouseLeave}
						onClick={handleChartClick}
					>
						{/* Filled area under the curve */}
						<path
							d={fillPath}
							fill={color}
							fillOpacity="0.02"
							stroke="none"
						/>

						{/* Main curve line */}
						<path
							d={pathData}
							fill="none"
							stroke={color}
							strokeWidth="2"
							vectorEffect="non-scaling-stroke"
						/>

						{/* Hover indicator lines */}
						{hoveredPoint !== null && (
							<g>
								{/* Vertical line */}
								<line
									x1={
										(hoveredPoint / (data.length - 1)) * 100
									}
									y1="0"
									x2={
										(hoveredPoint / (data.length - 1)) * 100
									}
									y2="100"
									stroke={color}
									strokeWidth="1"
									strokeDasharray="3,3"
									vectorEffect="non-scaling-stroke"
									opacity="0.7"
								/>
								{/* Horizontal line */}
								<line
									x1="0"
									y1={
										100 -
										((data[hoveredPoint].activeUsers -
											minValue) /
											range) *
											100
									}
									x2="100"
									y2={
										100 -
										((data[hoveredPoint].activeUsers -
											minValue) /
											range) *
											100
									}
									stroke={color}
									strokeWidth="1"
									strokeDasharray="3,3"
									vectorEffect="non-scaling-stroke"
									opacity="0.7"
								/>
							</g>
						)}
					</svg>
					<div className="chart-labels">
						<div className="y-axis-labels hourly-labels">
							{yLabels.map((value, index) => (
								<span
									key={value}
									style={{
										position: "absolute",
										top: `${(index / (yLabels.length - 1)) * 100}%`,
										transform: "translateY(-50%)",
									}}
								>
									{formatDetailedNumber(value)}
								</span>
							))}
						</div>
					</div>
					{/* X-axis hour labels */}
					<div className="x-axis-labels">
						{(() => {
							const hourlyLabels = filteredData
								.map((item, i) => ({ item, originalIndex: i }))
								.filter(({ item, originalIndex: i }) => {
									if (!item || !item.timestamp) return false;
									if (timeInterval === "5min")
										return i % 12 === 0;
									if (timeInterval === "15min")
										return i % 4 === 0;
									if (timeInterval === "30min")
										return i % 2 === 0;
									if (timeInterval === "1hour") return true;
									return false;
								});

							// On mobile, thin labels to ~6 max
							const isMobile = window.innerWidth < 768;
							const maxLabels = isMobile ? 6 : hourlyLabels.length;
							const step = maxLabels < hourlyLabels.length
								? Math.ceil(hourlyLabels.length / maxLabels)
								: 1;

							return hourlyLabels.filter((_, i) => i % step === 0);
						})()
							.map(({ item, originalIndex }, index) => {
								if (!item || !item.timestamp) return null;

								const x =
									(originalIndex / (filteredData.length - 1)) *
									100;

								let timeLabel;
								try {
									const isMobile = window.innerWidth < 768;
									timeLabel = new Date(
										item.timestamp,
									).toLocaleTimeString("en-US", {
										hour: "numeric",
										minute: isMobile ? undefined : (
											timeInterval === "1hour"
												? undefined
												: "2-digit"
										),
										hour12: true,
									});
								} catch (error) {
									console.error(
										"Error formatting time:",
										error,
									);
									timeLabel = "Invalid time";
								}

								return (
									<span
										key={`${item.timestamp}-${index}`}
										className="x-label"
										style={{
											position: "absolute",
											left: `${x}%`,
											transform:
												"translateX(-50%) rotate(-45deg)",
											transformOrigin: "top center",
											fontSize: window.innerWidth < 768 ? "0.6rem" : "0.75rem",
											color: "var(--text-secondary)",
											whiteSpace: "nowrap",
											marginTop: "8px",
										}}
									>
										{timeLabel}
									</span>
								);
							})}
					</div>

					{/* Tooltip */}
					{hoveredPoint !== null && (
						<div
							className="chart-tooltip"
							style={{
								position: "absolute",
								left: `${mousePosition.x + 10}px`,
								top: `${mousePosition.y - 10}px`,
								background: "rgba(0, 0, 0, 0.8)",
								color: "white",
								padding: "8px 12px",
								borderRadius: "6px",
								fontSize: "12px",
								pointerEvents: "none",
								zIndex: 1000,
								whiteSpace: "nowrap",
								boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
							}}
						>
							<div style={{ fontWeight: "bold" }}>
								{new Date(
									filteredData[hoveredPoint].timestamp,
								).toLocaleTimeString("en-US", {
									hour: "numeric",
									minute: "2-digit",
									hour12: true,
								})}
							</div>
							<div>
								{formatDetailedNumber(
									filteredData[hoveredPoint].activeUsers,
								)}{" "}
								Active Users
							</div>
						</div>
					)}
				</div>
			</div>
		);
	};

	// Dedicated DAU chart component with improved y-axis
	const DAUChart = ({ data, height = 300, color = "#667eea" }) => {
		const [hoveredPoint, setHoveredPoint] = useState(null);
		const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

		if (!data || data.length === 0) return <div>No data available</div>;

		const maxValue = Math.max(...data.map((d) => d.dau));
		const minValue = Math.min(...data.map((d) => d.dau));
		const range = maxValue - minValue || 1;

		// Create better y-axis labels with nice round numbers
		const createYAxisLabels = (min, max) => {
			const range = max - min;
			const step = Math.ceil(range / 4); // Create 4-5 labels
			const roundedMin = Math.floor(min / step) * step;
			const roundedMax = Math.ceil(max / step) * step;

			const labels = [];
			for (let i = roundedMax; i >= roundedMin; i -= step) {
				if (i >= min && i <= max) {
					labels.push(i);
				}
			}

			// Ensure we have at least min and max
			if (!labels.includes(max)) labels.unshift(max);
			if (!labels.includes(min)) labels.push(min);

			return labels.sort((a, b) => b - a); // Sort descending for top to bottom
		};

		const yLabels = createYAxisLabels(minValue, maxValue);

		// Create smooth curve using cubic bezier
		const createSmoothPath = (data) => {
			let path = "";
			for (let i = 0; i < data.length; i++) {
				const x = (i / (data.length - 1)) * 100;
				const y = 100 - ((data[i].dau - minValue) / range) * 100;

				if (i === 0) {
					path += `M ${x} ${y}`;
				} else {
					// Calculate control points for smooth curve
					const prevX = ((i - 1) / (data.length - 1)) * 100;
					const prevY =
						100 - ((data[i - 1].dau - minValue) / range) * 100;

					const cp1x = prevX + (x - prevX) * 0.3;
					const cp1y = prevY;
					const cp2x = x - (x - prevX) * 0.3;
					const cp2y = y;

					path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${x} ${y}`;
				}
			}
			return path;
		};

		const pathData = createSmoothPath(data);

		// Create filled area path
		const createFillPath = (data) => {
			let path = "";
			// Start from bottom left
			path += `M 0 100`;

			// Add the smooth curve
			for (let i = 0; i < data.length; i++) {
				const x = (i / (data.length - 1)) * 100;
				const y = 100 - ((data[i].dau - minValue) / range) * 100;

				if (i === 0) {
					path += ` L ${x} ${y}`;
				} else {
					// Calculate control points for smooth curve
					const prevX = ((i - 1) / (data.length - 1)) * 100;
					const prevY =
						100 - ((data[i - 1].dau - minValue) / range) * 100;

					const cp1x = prevX + (x - prevX) * 0.3;
					const cp1y = prevY;
					const cp2x = x - (x - prevX) * 0.3;
					const cp2y = y;

					path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${x} ${y}`;
				}
			}

			// Close the path by going to bottom right and back to start
			path += ` L 100 100 Z`;
			return path;
		};

		const fillPath = createFillPath(data);

		// Handle mouse movement over the chart
		const handleMouseMove = (event) => {
			const rect = event.currentTarget.getBoundingClientRect();
			const x = ((event.clientX - rect.left) / rect.width) * 100;
			// eslint-disable-next-line no-unused-vars
			const y = ((event.clientY - rect.top) / rect.height) * 100;

			// Find the closest data point
			const dataIndex = Math.round((x / 100) * (data.length - 1));
			const clampedIndex = Math.max(
				0,
				Math.min(data.length - 1, dataIndex),
			);

			setHoveredPoint(clampedIndex);
			setMousePosition({
				x: event.clientX - rect.left,
				y: event.clientY - rect.top,
			});
		};

		const handleMouseLeave = () => {
			setHoveredPoint(null);
		};

		return (
			<div
				className="chart-container dau-chart"
				style={{ height: height + 40, position: "relative" }}
			>
				<svg
					width="100%"
					height={height}
					viewBox="0 0 100 100"
					preserveAspectRatio="none"
					style={{ overflow: "hidden" }}
					onMouseMove={handleMouseMove}
					onMouseLeave={handleMouseLeave}
				>
					{/* Filled area under the curve */}
					<path
						d={fillPath}
						fill={color}
						fillOpacity="0.02"
						stroke="none"
					/>

					{/* Main curve line */}
					<path
						d={pathData}
						fill="none"
						stroke={color}
						strokeWidth="2"
						vectorEffect="non-scaling-stroke"
					/>

					{/* Hover indicator lines */}
					{hoveredPoint !== null && (
						<g>
							{/* Vertical line */}
							<line
								x1={(hoveredPoint / (data.length - 1)) * 100}
								y1="0"
								x2={(hoveredPoint / (data.length - 1)) * 100}
								y2="100"
								stroke={color}
								strokeWidth="1"
								strokeDasharray="3,3"
								vectorEffect="non-scaling-stroke"
								opacity="0.7"
							/>
							{/* Horizontal line */}
							<line
								x1="0"
								y1={
									100 -
									((data[hoveredPoint].dau - minValue) /
										range) *
										100
								}
								x2="100"
								y2={
									100 -
									((data[hoveredPoint].dau - minValue) /
										range) *
										100
								}
								stroke={color}
								strokeWidth="1"
								strokeDasharray="3,3"
								vectorEffect="non-scaling-stroke"
								opacity="0.7"
							/>
						</g>
					)}
				</svg>
				<div className="chart-labels">
					<div className="y-axis-labels dau-labels">
						{yLabels.map((value, index) => (
							<span
								key={value}
								style={{
									position: "absolute",
									top: `${(index / (yLabels.length - 1)) * 100}%`,
									transform: "translateY(-50%)",
								}}
							>
								{formatDetailedNumber(value)}
							</span>
						))}
					</div>
				</div>
				{/* X-axis date labels */}
				<div className="x-axis-labels">
					{data.map((item, index) => {
						const x = (index / (data.length - 1)) * 100;
						
						// Smart label filtering based on data length and screen width
						const isMobile = window.innerWidth < 768;
						const maxLabels = isMobile ? 6 : (data.length > 120 ? Math.ceil(data.length / 15) : data.length);
						const step = Math.max(1, Math.ceil(data.length / maxLabels));
						
						const shouldShowLabel = index % step === 0 || index === data.length - 1;
						
						if (!shouldShowLabel) return null;
						
						return (
							<span
								key={item.date}
								className="x-label"
								style={{
									position: "absolute",
									left: `${x}%`,
								}}
							>
								{item.date}
							</span>
						);
					}).filter(Boolean)}
				</div>

				{/* Tooltip */}
				{hoveredPoint !== null && (
					<div
						className="chart-tooltip"
						style={{
							position: "absolute",
							left: `${mousePosition.x + 10}px`,
							top: `${mousePosition.y - 10}px`,
							background: "rgba(0, 0, 0, 0.8)",
							color: "white",
							padding: "8px 12px",
							borderRadius: "6px",
							fontSize: "12px",
							pointerEvents: "none",
							zIndex: 1000,
							whiteSpace: "nowrap",
							boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
						}}
					>
						<div style={{ fontWeight: "bold" }}>
							{(() => {
								const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
								let date;
								let dayName = '';
								
								try {
									// Use fullDate if available (from backend or mock data)
									if (data[hoveredPoint].fullDate) {
										date = new Date(data[hoveredPoint].fullDate);
									} else {
										// Try parsing the date string with current year
										const currentYear = new Date().getFullYear();
										const dateStr = `${data[hoveredPoint].date} ${currentYear}`;
										date = new Date(dateStr);
									}
									
									// Validate the date
									if (!isNaN(date.getTime())) {
										dayName = dayNames[date.getDay()];
									}
								} catch (error) {
									console.warn('Error parsing date for tooltip:', error);
								}
								
								return dayName ? `${dayName}, ${data[hoveredPoint].date}` : data[hoveredPoint].date;
							})()}
						</div>
						<div>
							{formatDetailedNumber(data[hoveredPoint].dau)} DAU
						</div>
					</div>
				)}
			</div>
		);
	};

	const DailySessionChart = ({ data, height = 300, color = "#805AD5" }) => {
		const [hoveredPoint, setHoveredPoint] = useState(null);
		const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

		if (!data || data.length === 0) return <div>No data available</div>;

		const maxValue = Math.max(...data.map((d) => d.totalSessionTime));
		const minValue = 0;
		const range = maxValue - minValue || 1;

		// Create y-axis labels with time formatting
		const createYAxisLabels = () => {
			const steps = 5;
			const stepSize = Math.ceil(maxValue / (steps - 1));
			const labels = [];

			for (let i = 0; i < steps; i++) {
				const value = (steps - 1 - i) * stepSize;
				labels.push({
					value,
					label: formatTime(value),
				});
			}

			return labels;
		};

		const formatTime = (minutes) => {
			const hours = Math.floor(minutes / 60);
			const mins = minutes % 60;
			if (hours === 0) return `${mins}m`;
			if (mins === 0) return `${hours}h`;
			return `${hours}h ${mins}m`;
		};

		// Create smooth curve using cubic bezier
		const createSmoothPath = (data) => {
			let path = "";
			for (let i = 0; i < data.length; i++) {
				const x = (i / (data.length - 1)) * 100;
				const y =
					100 - ((data[i].totalSessionTime - minValue) / range) * 100;

				if (i === 0) {
					path += `M ${x} ${y}`;
				} else {
					const prevX = ((i - 1) / (data.length - 1)) * 100;
					const prevY =
						100 -
						((data[i - 1].totalSessionTime - minValue) / range) *
							100;

					const cp1x = prevX + (x - prevX) * 0.3;
					const cp1y = prevY;
					const cp2x = x - (x - prevX) * 0.3;
					const cp2y = y;

					path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${x} ${y}`;
				}
			}
			return path;
		};

		const createFillPath = (data) => {
			let path = `M 0 100 `;
			path += createSmoothPath(data);
			path += ` L 100 100 Z`;
			return path;
		};

		const handleMouseMove = (event) => {
			const rect = event.currentTarget.getBoundingClientRect();
			const x =
				((event.clientX - rect.left - 90) / (rect.width - 90)) * 100;

			const dataIndex = Math.round((x / 100) * (data.length - 1));
			const clampedIndex = Math.max(
				0,
				Math.min(data.length - 1, dataIndex),
			);

			setHoveredPoint(clampedIndex);
			setMousePosition({
				x: event.clientX - rect.left,
				y: event.clientY - rect.top,
			});
		};

		const handleMouseLeave = () => {
			setHoveredPoint(null);
		};

		const yLabels = createYAxisLabels();
		// const calculateAverageTime = (data) => {
		// 	if (!data || data.length === 0) return 0;
		// 	// Filter out zero values and undefined
		// 	const validData = data.filter((d) => d.totalSessionTime > 0);
		// 	if (validData.length === 0) return 0;

		// 	const sum = validData.reduce(
		// 		(acc, curr) => acc + curr.totalSessionTime,
		// 		0,
		// 	);
		// 	return Math.round(sum / validData.length);
		// };
		// Add CSS styles
		// Stats container styles matching the MAU chart
		// eslint-disable-next-line no-unused-vars
		const statsContainerStyle = {
			position: "absolute",
			top: "-40px",
			right: "0",
			display: "flex",
			gap: "8px",
		};

		// eslint-disable-next-line no-unused-vars
		const statBoxStyle = {
			background: "rgba(255, 255, 255, 0.05)",
			padding: "8px 16px",
			borderRadius: "6px",
			minWidth: "90px",
			textAlign: "center",
		};

		// eslint-disable-next-line no-unused-vars
		const statLabelStyle = {
			fontSize: "12px",
			color: "#A0AEC0",
			marginBottom: "4px",
			textTransform: "uppercase",
			letterSpacing: "0.5px",
		};

		// eslint-disable-next-line no-unused-vars
		const statValueStyle = {
			fontSize: "14px",
			color: "#FFFFFF",
			fontWeight: "500",
		};
		const isMobileYAxis = window.innerWidth < 768;
		const yLabelsContainerStyle = {
			position: "absolute",
			left: 0,
			top: 0,
			bottom: 40,
			width: isMobileYAxis ? "50px" : "90px",
			display: "flex",
			flexDirection: "column",
			justifyContent: "space-between",
			paddingRight: isMobileYAxis ? "5px" : "10px",
			boxSizing: "border-box",
		};

		const yLabelStyle = {
			textAlign: "right",
			fontSize: isMobileYAxis ? "9px" : "12px",
			color: "var(--text-tertiary, #718096)",
			whiteSpace: "nowrap",
			opacity: 0.8,
		};

		// eslint-disable-next-line no-unused-vars
		const averageTime = calculateAverageTime(data);
		// eslint-disable-next-line no-unused-vars
		const peakTime = Math.max(...data.map((d) => d.totalSessionTime));
		const isMobileSession = window.innerWidth < 768;
		return (
			<div
				className="chart-container daily-session-chart"
				style={{
					height: height + 40,
					position: "relative",
					paddingLeft: isMobileSession ? "50px" : "90px",
				}}
			>
				{/* Y-axis labels container */}
				<div style={yLabelsContainerStyle}>
					{yLabels.map((label, index) => (
						<div key={index} style={yLabelStyle}>
							{label.label}
						</div>
					))}
				</div>

				{/* Y-axis labels container */}
				<div
					style={{
						position: "absolute",
						left: 0,
						top: 0,
						bottom: 40,
						width: "90px",
						display: "flex",
						flexDirection: "column",
						justifyContent: "space-between",
						paddingRight: "10px",
						boxSizing: "border-box",
					}}
				>
					{yLabels.map((label, index) => (
						<div
							key={index}
							style={{
								textAlign: "right",
								fontSize: "12px",
								color: "var(--text-tertiary, #718096)",
								whiteSpace: "nowrap",
							}}
						>
							{label.label}
						</div>
					))}
				</div>

				<svg
					width="100%"
					height={height}
					viewBox="0 0 100 100"
					preserveAspectRatio="none"
					style={{ overflow: "visible" }}
					onMouseMove={handleMouseMove}
					onMouseLeave={handleMouseLeave}
				>
					{/* Filled area under the curve */}
					<path
						d={createFillPath(data)}
						fill={color}
						fillOpacity="0.02"
						stroke="none"
					/>

					{/* Main curve line */}
					<path
						d={createSmoothPath(data)}
						fill="none"
						stroke={color}
						strokeWidth="2"
						vectorEffect="non-scaling-stroke"
					/>

					{/* Hover indicator lines */}
					{hoveredPoint !== null && (
						<g>
							<line
								x1={(hoveredPoint / (data.length - 1)) * 100}
								y1="0"
								x2={(hoveredPoint / (data.length - 1)) * 100}
								y2="100"
								stroke={color}
								strokeWidth="1"
								strokeDasharray="3,3"
								vectorEffect="non-scaling-stroke"
								opacity="0.7"
							/>
							<line
								x1="0"
								y1={
									100 -
									((data[hoveredPoint].totalSessionTime -
										minValue) /
										range) *
										100
								}
								x2="100"
								y2={
									100 -
									((data[hoveredPoint].totalSessionTime -
										minValue) /
										range) *
										100
								}
								stroke={color}
								strokeWidth="1"
								strokeDasharray="3,3"
								vectorEffect="non-scaling-stroke"
								opacity="0.7"
							/>
						</g>
					)}
				</svg>

				{/* X-axis labels */}
				<div
					style={{
						position: "relative",
						height: "40px",
						marginTop: "10px",
					}}
				>
					{(() => {
						const isMobile = window.innerWidth < 768;
						const maxLabels = isMobile ? 6 : data.length;
						const step = Math.max(1, Math.ceil(data.length / maxLabels));
						
						return data.map((item, index) => {
							const shouldShow = index % step === 0 || index === data.length - 1;
							if (!shouldShow) return null;
							
							return (
								<span
									key={index}
									style={{
										position: "absolute",
										left: `${(index / (data.length - 1)) * 100}%`,
										transform: "translateX(-50%) rotate(-45deg)",
										transformOrigin: "top center",
										fontSize: isMobile ? "10px" : "12px",
										color: "var(--text-tertiary, #718096)",
									}}
								>
									{item.date}
								</span>
							);
						}).filter(Boolean);
					})()}
				</div>

				{/* Tooltip */}
				{hoveredPoint !== null && (
					<div
						style={{
							position: "absolute",
							left: `${mousePosition.x + 10}px`,
							top: `${mousePosition.y - 10}px`,
							background: "rgba(0, 0, 0, 0.8)",
							color: "white",
							padding: "8px 12px",
							borderRadius: "6px",
							fontSize: "12px",
							pointerEvents: "none",
							zIndex: 1000,
							whiteSpace: "nowrap",
							boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
						}}
					>
						<div style={{ fontWeight: "bold" }}>
							{data[hoveredPoint].date}
						</div>
						<div>
							Total Time:{" "}
							{formatTime(data[hoveredPoint].totalSessionTime)}
						</div>
					</div>
				)}
			</div>
		);
	};

	// Dedicated MAU chart component with improved styling to match DAU
	const MAUChart = ({ data, height = 250, color = "#48bb78" }) => {
		const [hoveredPoint, setHoveredPoint] = useState(null);
		const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

		if (!data || data.length === 0) return <div>No data available</div>;

		const maxValue = Math.max(...data.map((d) => d.mau));
		const minValue = 0; // Always start from 0 for MAU chart
		const range = maxValue - minValue || 1;

		// Create better y-axis labels with nice round numbers
		const createYAxisLabels = (min, max) => {
			const range = max - min;
			const step = Math.ceil(range / 4); // Create 4-5 labels
			const roundedMin = Math.floor(min / step) * step;
			const roundedMax = Math.ceil(max / step) * step;

			const labels = [];
			for (let i = roundedMax; i >= roundedMin; i -= step) {
				if (i >= min && i <= max) {
					labels.push(i);
				}
			}

			// Ensure we have at least min and max
			if (!labels.includes(max)) labels.unshift(max);
			if (!labels.includes(min)) labels.push(min);

			return labels.sort((a, b) => b - a); // Sort descending for top to bottom
		};

		const yLabels = createYAxisLabels(minValue, maxValue);

		// Create smooth curve using cubic bezier
		const createSmoothPath = (data) => {
			let path = "";
			for (let i = 0; i < data.length; i++) {
				const x = (i / (data.length - 1)) * 100;
				const y = 100 - ((data[i].mau - minValue) / range) * 100;

				if (i === 0) {
					path += `M ${x} ${y}`;
				} else {
					// Calculate control points for smooth curve
					const prevX = ((i - 1) / (data.length - 1)) * 100;
					const prevY =
						100 - ((data[i - 1].mau - minValue) / range) * 100;

					const cp1x = prevX + (x - prevX) * 0.3;
					const cp1y = prevY;
					const cp2x = x - (x - prevX) * 0.3;
					const cp2y = y;

					path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${x} ${y}`;
				}
			}
			return path;
		};

		const pathData = createSmoothPath(data);

		// Create filled area path
		const createFillPath = (data) => {
			let path = "";
			// Start from bottom left
			path += `M 0 100`;

			// Add the smooth curve
			for (let i = 0; i < data.length; i++) {
				const x = (i / (data.length - 1)) * 100;
				const y = 100 - ((data[i].mau - minValue) / range) * 100;

				if (i === 0) {
					path += ` L ${x} ${y}`;
				} else {
					// Calculate control points for smooth curve
					const prevX = ((i - 1) / (data.length - 1)) * 100;
					const prevY =
						100 - ((data[i - 1].mau - minValue) / range) * 100;

					const cp1x = prevX + (x - prevX) * 0.3;
					const cp1y = prevY;
					const cp2x = x - (x - prevX) * 0.3;
					const cp2y = y;

					path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${x} ${y}`;
				}
			}

			// Close the path by going to bottom right and back to start
			path += ` L 100 100 Z`;
			return path;
		};

		const fillPath = createFillPath(data);

		// Handle mouse movement over the chart
		const handleMouseMove = (event) => {
			const rect = event.currentTarget.getBoundingClientRect();
			const x = ((event.clientX - rect.left) / rect.width) * 100;
			// eslint-disable-next-line no-unused-vars
			const y = ((event.clientY - rect.top) / rect.height) * 100;

			// Find the closest data point - improved for monthly data
			const exactIndex = (x / 100) * (data.length - 1);
			const dataIndex = Math.round(exactIndex);
			const clampedIndex = Math.max(
				0,
				Math.min(data.length - 1, dataIndex),
			);

			// Always show hover for MAU chart since we have fewer data points
			setHoveredPoint(clampedIndex);
			setMousePosition({
				x: event.clientX - rect.left,
				y: event.clientY - rect.top,
			});
		};

		const handleMouseLeave = () => {
			setHoveredPoint(null);
		};

		return (
			<div
				className="chart-container mau-chart"
				style={{ height: height + 60, position: "relative" }}
			>
				<svg
					width="100%"
					height={height}
					viewBox="0 0 100 100"
					preserveAspectRatio="none"
					style={{ overflow: "visible" }}
					onMouseMove={handleMouseMove}
					onMouseLeave={handleMouseLeave}
				>
					{/* Filled area under the curve */}
					<path
						d={fillPath}
						fill={color}
						fillOpacity="0.02"
						stroke="none"
					/>

					{/* Main curve line */}
					<path
						d={pathData}
						fill="none"
						stroke={color}
						strokeWidth="2"
						vectorEffect="non-scaling-stroke"
					/>

					{/* Hover indicator lines */}
					{hoveredPoint !== null && (
						<g>
							{/* Vertical line */}
							<line
								x1={(hoveredPoint / (data.length - 1)) * 100}
								y1="0"
								x2={(hoveredPoint / (data.length - 1)) * 100}
								y2="100"
								stroke={color}
								strokeWidth="1"
								strokeDasharray="3,3"
								vectorEffect="non-scaling-stroke"
								opacity="0.7"
							/>
							{/* Horizontal line */}
							<line
								x1="0"
								y1={
									100 -
									((data[hoveredPoint].mau - minValue) /
										range) *
										100
								}
								x2="100"
								y2={
									100 -
									((data[hoveredPoint].mau - minValue) /
										range) *
										100
								}
								stroke={color}
								strokeWidth="1"
								strokeDasharray="3,3"
								vectorEffect="non-scaling-stroke"
								opacity="0.7"
							/>
						</g>
					)}
				</svg>
				<div className="chart-labels">
					<div className="y-axis-labels mau-labels">
						{yLabels.map((value, index) => (
							<span
								key={value}
								style={{
									position: "absolute",
									top: `${(index / (yLabels.length - 1)) * 100}%`,
									transform: "translateY(-50%)",
								}}
							>
								{formatDetailedNumber(value)}
							</span>
						))}
					</div>
				</div>
				{/* X-axis month labels */}
				<div className="x-axis-labels">
					{(() => {
						const isMobile = window.innerWidth < 768;
						const maxLabels = isMobile ? 6 : data.length;
						const step = Math.max(1, Math.ceil(data.length / maxLabels));

						return data.map((item, index) => {
							if (data.length > 8 && !(index % step === 0 || index === data.length - 1)) return null;
							const x = (index / (data.length - 1)) * 100;
							return (
								<span
									key={item.month}
									className="x-label"
									style={{
										position: "absolute",
										left: `${x}%`,
									}}
								>
									{item.month}
								</span>
							);
						}).filter(Boolean);
					})()}
				</div>

				{/* Tooltip */}
				{hoveredPoint !== null && (
					<div
						className="chart-tooltip"
						style={{
							position: "absolute",
							left: `${mousePosition.x + 10}px`,
							top: `${mousePosition.y - 10}px`,
							background: "rgba(0, 0, 0, 0.8)",
							color: "white",
							padding: "8px 12px",
							borderRadius: "6px",
							fontSize: "12px",
							pointerEvents: "none",
							zIndex: 1000,
							whiteSpace: "nowrap",
							boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
						}}
					>
						<div style={{ fontWeight: "bold" }}>
							{data[hoveredPoint].month}
						</div>
						<div>
							{formatDetailedNumber(data[hoveredPoint].mau)} MAU
						</div>
					</div>
				)}
			</div>
		);
	};

	// Simple line chart component
	const LineChart = ({
		data,
		dataKey,
		height = 200,
		color = "#667eea",
		showGrid = true,
		showDataPoints = true,
		smoothCurve = false,
	}) => {
		if (!data || data.length === 0) return <div>No data available</div>;

		const maxValue = Math.max(...data.map((d) => d[dataKey]));
		const minValue = Math.min(...data.map((d) => d[dataKey]));
		const range = maxValue - minValue || 1;

		// Create smooth curve using cubic bezier if smoothCurve is enabled
		const createPath = (data) => {
			if (!smoothCurve || data.length < 2) {
				// Original polyline for non-smooth curves
				const points = data
					.map((item, index) => {
						const x = (index / (data.length - 1)) * 100;
						const y =
							100 - ((item[dataKey] - minValue) / range) * 100;
						return `${x},${y}`;
					})
					.join(" ");
				return `M ${points.replace(/,/g, " L ").replace(/L /, "")}`;
			}

			// Smooth curve using cubic bezier
			let path = "";
			for (let i = 0; i < data.length; i++) {
				const x = (i / (data.length - 1)) * 100;
				const y = 100 - ((data[i][dataKey] - minValue) / range) * 100;

				if (i === 0) {
					path += `M ${x} ${y}`;
				} else {
					// Calculate control points for smooth curve
					const prevX = ((i - 1) / (data.length - 1)) * 100;
					const prevY =
						100 - ((data[i - 1][dataKey] - minValue) / range) * 100;

					const cp1x = prevX + (x - prevX) * 0.3;
					const cp1y = prevY;
					const cp2x = x - (x - prevX) * 0.3;
					const cp2y = y;

					path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${x} ${y}`;
				}
			}
			return path;
		};

		const pathData = createPath(data);

		return (
			<div className="chart-container" style={{ height: height + 40 }}>
				<svg
					width="100%"
					height={height}
					viewBox="0 0 100 100"
					preserveAspectRatio="none"
				>
					{showGrid && (
						<g className="grid">
							{[0, 25, 50, 75, 100].map((y) => (
								<line
									key={y}
									x1="0"
									y1={y}
									x2="100"
									y2={y}
									stroke="#f1f5f9"
									strokeWidth="0.2"
									strokeOpacity="0.4"
								/>
							))}
						</g>
					)}

					{smoothCurve ? (
						<path
							d={pathData}
							fill="none"
							stroke={color}
							strokeWidth="2"
							vectorEffect="non-scaling-stroke"
						/>
					) : (
						<polyline
							fill="none"
							stroke={color}
							strokeWidth="2"
							points={data
								.map((item, index) => {
									const x = (index / (data.length - 1)) * 100;
									const y =
										100 -
										((item[dataKey] - minValue) / range) *
											100;
									return `${x},${y}`;
								})
								.join(" ")}
							vectorEffect="non-scaling-stroke"
						/>
					)}

					{showDataPoints &&
						data.map((item, index) => {
							const x = (index / (data.length - 1)) * 100;
							const y =
								100 -
								((item[dataKey] - minValue) / range) * 100;
							return (
								<circle
									key={index}
									cx={x}
									cy={y}
									r="1.5"
									fill={color}
									className="data-point"
									vectorEffect="non-scaling-stroke"
								>
									<title>{`${item.date || item.month}: ${formatDetailedNumber(item[dataKey])}`}</title>
								</circle>
							);
						})}
				</svg>
				{showGrid && (
					<div className="chart-labels">
						<div className="y-axis-labels">
							<span>{formatDetailedNumber(maxValue)}</span>
							<span>
								{formatDetailedNumber(
									Math.floor((maxValue + minValue) / 2),
								)}
							</span>
							<span>{formatDetailedNumber(minValue)}</span>
						</div>
					</div>
				)}
			</div>
		);
	};

	// Workspace Activity Stacked Bar Chart Component - FIXED RENDERING
	const WorkspaceActivityChart = ({ data, height = 300, onBarClick }) => {
		const [hoveredData, setHoveredData] = useState(null);
		const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
		const chartRef = useRef(null);

		// Fix: Update dimensions when component mounts and resizes
		useEffect(() => {
			const updateDimensions = () => {
				if (chartRef.current) {
					const rect = chartRef.current.getBoundingClientRect();
					setDimensions({ 
						width: rect.width || chartRef.current.clientWidth || 800, // fallback width
						height: rect.height || height 
					});
				}
			};

			updateDimensions();
			window.addEventListener('resize', updateDimensions);
			
			// Also update after a short delay to ensure DOM is ready
			const timer = setTimeout(updateDimensions, 100);

			return () => {
				window.removeEventListener('resize', updateDimensions);
				clearTimeout(timer);
			};
		}, [data, height]);

		if (!data || data.length === 0) {
			return (
				<div className="chart-container" style={{ height: height + 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
					<div>No workspace activity data available</div>
				</div>
			);
		}

		const maxValue = Math.max(...data.map(item => item.totalWorkspaces));
		const yLabels = Array.from({ length: 6 }, (_, i) => Math.ceil((maxValue / 5) * i));
		
		// Use reliable width calculation
		const chartWidth = dimensions.width || 800; // fallback to 800px
		const availableWidth = chartWidth - 80; // subtract margins
		
		// Calculate proper positioning
		const chartArea = {
			left: 60,
			right: chartWidth - 20,
			top: 20,
			bottom: height - 50
		};

		const handleMouseMove = (event) => {
			if (!chartRef.current || availableWidth <= 0) return;
			
			const rect = chartRef.current.getBoundingClientRect();
			const x = event.clientX - rect.left;
			const barSpacing = (chartArea.right - chartArea.left) / data.length;
			const index = Math.floor((x - chartArea.left) / barSpacing);
			
			if (index >= 0 && index < data.length) {
				setHoveredData({
					...data[index],
					index,
					mouseX: event.clientX - rect.left,
					mouseY: event.clientY - rect.top,
				});
			}
		};

		const handleMouseLeave = () => {
			setHoveredData(null);
		};

		return (
			<div className="chart-container" style={{ 
				height: height + 60, 
				position: 'relative',
				minWidth: '400px', // Ensure minimum width for readability
				overflow: 'hidden' // Prevent layout issues
			}}>
				<svg
					ref={chartRef}
					width="100%"
					height={height}
					onMouseMove={handleMouseMove}
					onMouseLeave={handleMouseLeave}
					style={{ overflow: 'visible' }}
				>
					{/* Grid lines */}
					{yLabels.map((label, i) => {
						const y = chartArea.bottom - (label / maxValue) * (chartArea.bottom - chartArea.top);
						return (
							<line
								key={`grid-${i}`}
								x1={chartArea.left}
								y1={y}
								x2={chartArea.right}
								y2={y}
								stroke="var(--border-light)"
								strokeWidth="1"
								strokeOpacity="0.2"
							/>
						);
					})}

					{/* Gradient Definitions */}
					<defs>
						<linearGradient id="activeBarGradient" x1="0%" y1="0%" x2="0%" y2="100%">
							<stop offset="0%" stopColor="#34d399" />
							<stop offset="100%" stopColor="#10b981" />
						</linearGradient>
						<linearGradient id="inactiveBarGradient" x1="0%" y1="0%" x2="0%" y2="100%">
							<stop offset="0%" stopColor="#f87171" />
							<stop offset="100%" stopColor="#ef4444" />
						</linearGradient>
						<filter id="barGlow" x="-50%" y="-50%" width="200%" height="200%">
							<feGaussianBlur stdDeviation="3" result="coloredBlur"/>
							<feMerge>
								<feMergeNode in="coloredBlur"/>
								<feMergeNode in="SourceGraphic"/>
							</feMerge>
						</filter>
					</defs>

					{/* Stacked Bars */}
					{data.map((item, index) => {
						// Use chartArea for consistent positioning
						const barSpacing = (chartArea.right - chartArea.left) / data.length;
						const barWidth = barSpacing * 0.5; // 50% for cleaner look
						const x = chartArea.left + (index * barSpacing) + (barSpacing - barWidth) / 2;
						
						// Ensure we have valid data
						const workspacesWithActivity = item.workspacesWithActivity || 0;
						const workspacesWithNoActivity = item.workspacesWithNoActivity || 0;
						const totalWorkspaces = workspacesWithActivity + workspacesWithNoActivity;
						
						// Calculate heights using chartArea (ensure they're not negative)
						const chartHeight = chartArea.bottom - chartArea.top;
						const totalHeight = Math.max(0, (totalWorkspaces / maxValue) * chartHeight);
						const activeHeight = Math.max(0, (workspacesWithActivity / maxValue) * chartHeight);
						const inactiveHeight = Math.max(0, (workspacesWithNoActivity / maxValue) * chartHeight);
						
						const isHovered = hoveredData && hoveredData.index === index;
						const cornerRadius = Math.min(barWidth / 4, 6); // Rounded corners
						
						// Only render if we have valid dimensions
						if (barWidth <= 0 || availableWidth <= 0) return null;
						
						return (
							<g key={index} style={{ transition: 'all 0.2s ease' }}>
								{/* Active workspaces bar (bottom, green) */}
								<rect
									x={x}
									y={chartArea.bottom - activeHeight}
									width={barWidth}
									height={activeHeight}
									rx={inactiveHeight > 0 ? 0 : cornerRadius}
									ry={inactiveHeight > 0 ? 0 : cornerRadius}
									fill="url(#activeBarGradient)"
									opacity={isHovered ? 1 : 0.85}
									filter={isHovered ? "url(#barGlow)" : "none"}
									style={{ 
										cursor: activeHeight > 0 ? 'pointer' : 'default',
										transition: 'all 0.2s ease',
										transform: isHovered ? 'scaleX(1.05)' : 'scaleX(1)',
										transformOrigin: `${x + barWidth/2}px center`
									}}
									onClick={() => {
										if (onBarClick && activeHeight > 0) {
											onBarClick(item.date, 'active');
										}
									}}
								/>
								{/* Inactive workspaces bar (top, red) */}
								<rect
									x={x}
									y={chartArea.bottom - totalHeight}
									width={barWidth}
									height={inactiveHeight}
									rx={cornerRadius}
									ry={cornerRadius}
									fill="url(#inactiveBarGradient)"
									opacity={isHovered ? 1 : 0.85}
									filter={isHovered ? "url(#barGlow)" : "none"}
									style={{ 
										cursor: inactiveHeight > 0 ? 'pointer' : 'default',
										transition: 'all 0.2s ease',
										transform: isHovered ? 'scaleX(1.05)' : 'scaleX(1)',
										transformOrigin: `${x + barWidth/2}px center`
									}}
									onClick={() => {
										if (onBarClick && inactiveHeight > 0) {
											onBarClick(item.date, 'inactive');
										}
									}}
								/>
							</g>
						);
					})}
				</svg>

				{/* Y-axis labels */}
				<div style={{
					position: 'absolute',
					left: 0,
					top: chartArea.top,
					height: `${chartArea.bottom - chartArea.top}px`,
					width: '50px',
					pointerEvents: 'none',
				}}>
					{yLabels.map((label, i) => {
						// Calculate Y position from bottom to top
						const yPosition = chartArea.bottom - chartArea.top - (i * (chartArea.bottom - chartArea.top) / (yLabels.length - 1));
						return (
							<span 
								key={i}
								style={{
									position: 'absolute',
									right: '10px',
									top: `${yPosition - 6}px`, // -6px to center the text
									fontSize: '11px',
									color: 'var(--text-tertiary)',
									textAlign: 'right',
									lineHeight: '12px',
									fontWeight: '500'
								}}
							>
								{label}
							</span>
						);
					})}
				</div>

				{/* X-axis labels */}
				<div style={{
					position: 'absolute',
					bottom: '10px',
					left: chartArea.left,
					width: `${chartArea.right - chartArea.left}px`,
					height: '30px',
					pointerEvents: 'none',
				}}>
					{data.map((item, index) => {
						// Calculate exact bar position to align labels
						const barSpacing = (chartArea.right - chartArea.left) / data.length;
						const barWidth = barSpacing * 0.6;
						const barX = (index * barSpacing) + (barSpacing - barWidth) / 2;
						const labelX = barX + barWidth / 2; // Center of the bar
						
						// Show every Nth label to avoid crowding; fewer on mobile
						const isMobile = window.innerWidth < 768;
						const maxLabels = isMobile ? 5 : 8;
						const shouldShow = index % Math.max(1, Math.ceil(data.length / maxLabels)) === 0 || 
										 index === data.length - 1 ||
										 data.length <= maxLabels;
						
						return shouldShow ? (
							<div
								key={index}
								style={{
									position: 'absolute',
									left: `${labelX}px`,
									transform: 'translateX(-50%)',
									top: '5px',
									fontSize: '10px',
									color: 'var(--text-tertiary)',
									whiteSpace: 'nowrap',
									fontWeight: '500',
									textAlign: 'center'
								}}
							>
								{item.displayDate}
							</div>
						) : null;
					})}
				</div>

				{/* Modern Tooltip */}
				{hoveredData && (
					<div style={{
						position: 'absolute',
						left: `${Math.min(hoveredData.mouseX + 15, chartWidth - 200)}px`,
						top: `${hoveredData.mouseY - 10}px`,
						background: 'linear-gradient(165deg, rgba(30, 30, 45, 0.98) 0%, rgba(20, 20, 30, 0.98) 100%)',
						color: 'white',
						padding: '16px 20px',
						borderRadius: '16px',
						fontSize: '13px',
						pointerEvents: 'none',
						zIndex: 1000,
						boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4), 0 0 60px rgba(99, 102, 241, 0.1)',
						border: '1px solid rgba(99, 102, 241, 0.2)',
						backdropFilter: 'blur(20px)',
						minWidth: '180px'
					}}>
						<div style={{ 
							fontWeight: '700', 
							marginBottom: '12px', 
							fontSize: '14px',
							background: 'linear-gradient(135deg, #fafafa 0%, #a1a1aa 100%)',
							WebkitBackgroundClip: 'text',
							WebkitTextFillColor: 'transparent',
							backgroundClip: 'text'
						}}>
							{hoveredData.displayDate}
						</div>
						<div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
							<div style={{ 
								width: '14px', 
								height: '14px', 
								background: 'linear-gradient(135deg, #34d399 0%, #10b981 100%)', 
								marginRight: '10px', 
								borderRadius: '4px',
								boxShadow: '0 0 10px rgba(16, 185, 129, 0.4)'
							}}></div>
							<span style={{ color: '#34d399', fontWeight: '600' }}>Active:</span>
							<span style={{ marginLeft: '6px', fontWeight: '700' }}>{hoveredData.workspacesWithActivity}</span>
						</div>
						<div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
							<div style={{ 
								width: '14px', 
								height: '14px', 
								background: 'linear-gradient(135deg, #f87171 0%, #ef4444 100%)', 
								marginRight: '10px', 
								borderRadius: '4px',
								boxShadow: '0 0 10px rgba(239, 68, 68, 0.4)'
							}}></div>
							<span style={{ color: '#f87171', fontWeight: '600' }}>Inactive:</span>
							<span style={{ marginLeft: '6px', fontWeight: '700' }}>{hoveredData.workspacesWithNoActivity}</span>
						</div>
						<div style={{ 
							fontSize: '12px', 
							color: '#71717a', 
							marginTop: '10px', 
							paddingTop: '10px',
							borderTop: '1px solid rgba(255, 255, 255, 0.1)',
							fontWeight: '500'
						}}>
							Total: <span style={{ color: '#a5b4fc', fontWeight: '700' }}>{hoveredData.totalWorkspaces}</span> workspaces
						</div>
					</div>
				)}

				{/* Modern Legend */}
				<div style={{
					position: 'absolute',
					bottom: '45px',
					right: '24px',
					display: 'flex',
					gap: '16px',
					fontSize: '12px',
					background: 'linear-gradient(165deg, rgba(30, 30, 45, 0.9) 0%, rgba(20, 20, 30, 0.9) 100%)',
					padding: '12px 18px',
					borderRadius: '14px',
					border: '1px solid rgba(99, 102, 241, 0.2)',
					boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
					backdropFilter: 'blur(10px)'
				}}>
					<div style={{ 
						display: 'flex', 
						alignItems: 'center',
						fontWeight: '600'
					}}>
						<div style={{ 
							width: '16px', 
							height: '16px', 
							background: 'linear-gradient(135deg, #34d399 0%, #10b981 100%)', 
							marginRight: '8px', 
							borderRadius: '5px',
							boxShadow: '0 0 10px rgba(16, 185, 129, 0.4)'
						}}></div>
						<span style={{ color: '#34d399' }}>Active</span>
					</div>
					<div style={{ 
						display: 'flex', 
						alignItems: 'center',
						fontWeight: '600'
					}}>
						<div style={{ 
							width: '16px', 
							height: '16px', 
							background: 'linear-gradient(135deg, #f87171 0%, #ef4444 100%)', 
							marginRight: '8px', 
							borderRadius: '5px',
							boxShadow: '0 0 10px rgba(239, 68, 68, 0.4)'
						}}></div>
						<span style={{ color: '#f87171' }}>Inactive</span>
					</div>
				</div>
			</div>
		);
	};

	// Dedicated Engagement Chart component with improved styling to match DAU/MAU
	const EngagementChart = ({ data, metrics, height = 250 }) => {
		const [hoveredPoint, setHoveredPoint] = useState(null);
		const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

		if (!data || data.length === 0) return <div>No data available</div>;

		const colors = {
			interactions: "#667eea",
			dmMessages: "#48bb78",
			stackMessages: "#ed8936",
		};

		const metricLabels = {
			interactions: "Interactions",
			dmMessages: "DM Messages",
			stackMessages: "Stack Messages",
		};

		const allValues = data.flatMap((item) =>
			metrics.map((metric) => item[metric]),
		);
		const maxValue = Math.max(...allValues);
		const minValue = 0; // Always start from 0 for engagement chart
		const range = maxValue - minValue || 1;

		// Create better y-axis labels with nice round numbers
		const createYAxisLabels = (min, max) => {
			const range = max - min;
			const step = Math.ceil(range / 4); // Create 4-5 labels
			const roundedMin = Math.floor(min / step) * step;
			const roundedMax = Math.ceil(max / step) * step;

			const labels = [];
			for (let i = roundedMax; i >= roundedMin; i -= step) {
				if (i >= min && i <= max) {
					labels.push(i);
				}
			}

			// Ensure we have at least min and max
			if (!labels.includes(max)) labels.unshift(max);
			if (!labels.includes(min)) labels.push(min);

			return labels.sort((a, b) => b - a); // Sort descending for top to bottom
		};

		const yLabels = createYAxisLabels(minValue, maxValue);

		// Create smooth curve using cubic bezier for each metric
		const createSmoothPath = (data, metric) => {
			let path = "";
			for (let i = 0; i < data.length; i++) {
				const x = (i / (data.length - 1)) * 100;
				const y = 100 - ((data[i][metric] - minValue) / range) * 100;

				if (i === 0) {
					path += `M ${x} ${y}`;
				} else {
					// Calculate control points for smooth curve
					const prevX = ((i - 1) / (data.length - 1)) * 100;
					const prevY =
						100 - ((data[i - 1][metric] - minValue) / range) * 100;

					const cp1x = prevX + (x - prevX) * 0.3;
					const cp1y = prevY;
					const cp2x = x - (x - prevX) * 0.3;
					const cp2y = y;

					path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${x} ${y}`;
				}
			}
			return path;
		};

		// Create filled area path for each metric
		const createFillPath = (data, metric) => {
			let path = "";
			// Start from bottom left
			path += `M 0 100`;

			// Add the smooth curve
			for (let i = 0; i < data.length; i++) {
				const x = (i / (data.length - 1)) * 100;
				const y = 100 - ((data[i][metric] - minValue) / range) * 100;

				if (i === 0) {
					path += ` L ${x} ${y}`;
				} else {
					// Calculate control points for smooth curve
					const prevX = ((i - 1) / (data.length - 1)) * 100;
					const prevY =
						100 - ((data[i - 1][metric] - minValue) / range) * 100;

					const cp1x = prevX + (x - prevX) * 0.3;
					const cp1y = prevY;
					const cp2x = x - (x - prevX) * 0.3;
					const cp2y = y;

					path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${x} ${y}`;
				}
			}

			// Close the path by going to bottom right and back to start
			path += ` L 100 100 Z`;
			return path;
		};

		// Handle mouse movement over the chart
		const handleMouseMove = (event) => {
			const rect = event.currentTarget.getBoundingClientRect();
			const x = ((event.clientX - rect.left) / rect.width) * 100;

			// Find the closest data point
			const dataIndex = Math.round((x / 100) * (data.length - 1));
			const clampedIndex = Math.max(0, Math.min(data.length - 1, dataIndex));

			setHoveredPoint(clampedIndex);
			setMousePosition({
				x: event.clientX - rect.left,
				y: event.clientY - rect.top,
			});
		};

		const handleMouseLeave = () => {
			setHoveredPoint(null);
		};

		return (
			<div
				className="chart-container engagement-chart"
				style={{ height: height + 60, position: "relative" }}
			>
				<svg
					width="100%"
					height={height}
					viewBox="0 0 100 100"
					preserveAspectRatio="none"
					style={{ overflow: "hidden", cursor: "crosshair" }}
					onMouseMove={handleMouseMove}
					onMouseLeave={handleMouseLeave}
				>
					{/* Filled areas under the curves */}
					{metrics.map((metric) => (
						<path
							key={`fill-${metric}`}
							d={createFillPath(data, metric)}
							fill={colors[metric]}
							fillOpacity="0.02"
							stroke="none"
						/>
					))}

					{/* Main curve lines */}
					{metrics.map((metric) => (
						<path
							key={`line-${metric}`}
							d={createSmoothPath(data, metric)}
							fill="none"
							stroke={colors[metric]}
							strokeWidth="2"
							vectorEffect="non-scaling-stroke"
						/>
					))}

					{/* Hover indicator lines */}
					{hoveredPoint !== null && (
						<g>
							{/* Vertical line */}
							<line
								x1={(hoveredPoint / (data.length - 1)) * 100}
								y1="0"
								x2={(hoveredPoint / (data.length - 1)) * 100}
								y2="100"
								stroke="#a0aec0"
								strokeWidth="1"
								strokeDasharray="3,3"
								vectorEffect="non-scaling-stroke"
								opacity="0.7"
							/>
							{/* Horizontal lines for each metric */}
							{metrics.map((metric) => (
								<line
									key={`h-line-${metric}`}
									x1="0"
									y1={100 - ((data[hoveredPoint][metric] - minValue) / range) * 100}
									x2="100"
									y2={100 - ((data[hoveredPoint][metric] - minValue) / range) * 100}
									stroke={colors[metric]}
									strokeWidth="1"
									strokeDasharray="3,3"
									vectorEffect="non-scaling-stroke"
									opacity="0.5"
								/>
							))}
							{/* Data point circles */}
							{metrics.map((metric) => (
								<circle
									key={`point-${metric}`}
									cx={(hoveredPoint / (data.length - 1)) * 100}
									cy={100 - ((data[hoveredPoint][metric] - minValue) / range) * 100}
									r="0.8"
									fill={colors[metric]}
									stroke="white"
									strokeWidth="0.3"
									vectorEffect="non-scaling-stroke"
								/>
							))}
						</g>
					)}
				</svg>
				<div className="chart-labels">
					<div className="y-axis-labels engagement-labels">
						{yLabels.map((value, index) => (
							<span
								key={value}
								style={{
									position: "absolute",
									top: `${(index / (yLabels.length - 1)) * 100}%`,
									transform: "translateY(-50%)",
								}}
							>
								{formatDetailedNumber(value)}
							</span>
						))}
					</div>
				</div>
				{/* X-axis month labels */}
				<div className="x-axis-labels">
					{(() => {
						const isMobile = window.innerWidth < 768;
						const maxLabels = isMobile ? 6 : data.length;
						const step = Math.max(1, Math.ceil(data.length / maxLabels));

						return data.map((item, index) => {
							if (data.length > 8 && !(index % step === 0 || index === data.length - 1)) return null;
							const x = (index / (data.length - 1)) * 100;
							return (
								<span
									key={item.month}
									className="x-label"
									style={{
										position: "absolute",
										left: `${x}%`,
										transform: "translateX(-50%)",
									}}
								>
									{item.month}
								</span>
							);
						}).filter(Boolean);
					})()}
				</div>

				{/* Tooltip */}
				{hoveredPoint !== null && (
					<div
						className="chart-tooltip"
						style={{
							position: "absolute",
							left: `${mousePosition.x + 15}px`,
							top: `${mousePosition.y + 15}px`,
							background: "rgba(0, 0, 0, 0.85)",
							color: "white",
							padding: "10px 14px",
							borderRadius: "8px",
							fontSize: "12px",
							pointerEvents: "none",
							zIndex: 1000,
							whiteSpace: "nowrap",
							boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
							minWidth: "140px",
							transform: mousePosition.x > 250 ? "translateX(-110%)" : "none",
						}}
					>
						<div style={{ fontWeight: "bold", marginBottom: "8px", borderBottom: "1px solid rgba(255,255,255,0.2)", paddingBottom: "6px" }}>
							{data[hoveredPoint].month}
						</div>
						{metrics.map((metric) => (
							<div
								key={`tooltip-${metric}`}
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									marginTop: "4px",
								}}
							>
								<div style={{ display: "flex", alignItems: "center" }}>
									<span
										style={{
											width: "8px",
											height: "8px",
											borderRadius: "50%",
											backgroundColor: colors[metric],
											marginRight: "6px",
											display: "inline-block",
										}}
									></span>
									<span style={{ color: "#a0aec0" }}>{metricLabels[metric]}:</span>
								</div>
								<strong style={{ marginLeft: "10px" }}>
									{formatDetailedNumber(data[hoveredPoint][metric])}
								</strong>
							</div>
						))}
					</div>
				)}

				{/* Legend */}
				<div className="chart-legend">
					{metrics.map((metric) => (
						<div key={metric} className="legend-item">
							<div
								className="legend-color"
								style={{ backgroundColor: colors[metric] }}
							></div>
							<span>
								{metric === "dmMessages"
									? "DM Messages"
									: metric === "stackMessages"
										? "Stack Messages"
										: "Interactions"}
							</span>
						</div>
					))}
				</div>
			</div>
		);
	};

	if (loading)
		return (
			<div className="analytics-container">
				<GlowOrbs />
				<div className="loading" style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
					<div className="loading-spinner" style={{ width: '48px', height: '48px' }}></div>
					<span style={{ color: '#94a3b8' }}>Loading analytics data...</span>
				</div>
			</div>
		);
	if (error) return (
		<div className="analytics-container">
			<GlowOrbs />
			<div className="error">Error: {error}</div>
		</div>
	);

	const { monthlyData, dailyData, currentMetrics } = analyticsData;

	return (
		<div className="analytics-container">
			{/* Animated Glow Orbs Background */}
			<GlowOrbs />
			
			{/* Content wrapper */}
			<motion.div 
				className="analytics-content"
				initial="hidden"
				animate="visible"
				variants={containerVariants}
			>
				{/* Header Section */}
				<motion.div className="analytics-header" variants={itemVariants}>
					<h1>Analytics <span className="gradient-text">Dashboard</span></h1>
					<p>Monitor your workspace performance and user engagement in real-time</p>
				</motion.div>

			{/* Key Metrics Grid - Row 1: DAU and MAU */}
				<motion.div className="metrics-grid" variants={containerVariants}>
					<motion.div
					className="metric-card success clickable"
					onClick={fetchActiveUsers}
						variants={cardVariants}
						whileHover="hover"
						whileTap={{ scale: 0.98 }}
				>
					<div className="metric-header">
							<motion.div 
								className="metric-value"
								initial={{ opacity: 0, scale: 0.5 }}
								animate={{ opacity: 1, scale: 1 }}
								transition={{ duration: 0.5, delay: 0.2 }}
							>
							{formatNumber(currentMetrics.currentActiveUsers)}
							</motion.div>
							<div className={`status-badge live${liveStats.degraded ? " degraded" : ""}`}>
								{liveStats.degraded ? "STALE" : "LIVE"}
							</div>
					</div>
					<div className="metric-label">Current Active Users</div>
					{liveStats.onlineUsers > 0 && (
						<div className="live-stats-sub">
							across {liveStats.liveWorkspaces} {liveStats.liveWorkspaces === 1 ? "workspace" : "workspaces"}
							{liveStats.updatedAt && (
								<span className="live-stats-updated">
									{" · updated "}
									{Math.max(0, Math.round((Date.now() - liveStats.updatedAt) / 1000))}s ago
								</span>
							)}
						</div>
					)}
					{liveStats.degraded && (
						<div className="live-stats-degraded">
							Live data unavailable — showing cached/last-seen counts
						</div>
					)}
					</motion.div>
				<ActiveUsersModal
					isOpen={isActiveUsersModalOpen}
					onClose={() => setIsActiveUsersModalOpen(false)}
					activeUsers={activeUsers}
					modalType="CURRENT"
					isLoading={isModalLoading}
				/>
				
				{/* DAU Card */}
				<motion.div
					className="metric-card primary clickable"
					onClick={fetchDauUsers}
					variants={cardVariants}
					whileHover="hover"
					whileTap={{ scale: 0.98 }}
				>
					<div className="metric-header">
						<motion.div 
							className="metric-value"
							initial={{ opacity: 0, scale: 0.5 }}
							animate={{ opacity: 1, scale: 1 }}
							transition={{ duration: 0.5, delay: 0.3 }}
						>
							{formatNumber(currentMetrics.todayDAU)}
						</motion.div>
						<div className="metric-change positive">
							{calculatePercentageChange(
								currentMetrics.todayDAU,
								currentMetrics.yesterdayDAU,
							)}
						</div>
					</div>
					<div className="metric-label">Daily Active Users (DAU)</div>
					<div className="metric-chart">
						<LineChart
							data={dailyData.slice(-7)}
							dataKey="dau"
							height={60}
							color="#3b82f6"
							showGrid={false}
						/>
					</div>
				</motion.div>
				<ActiveUsersModal
					isOpen={isModalOpen}
					onClose={() => setIsModalOpen(false)}
					activeUsers={dauUsers}
					modalType="DAU"
					isLoading={isModalLoading}
				/>

				{/* MAU Card */}
				<motion.div 
					className="metric-card success clickable" 
					onClick={fetchMauUsers}
					variants={cardVariants}
					whileHover="hover"
					whileTap={{ scale: 0.98 }}
				>
					<div className="metric-header">
						<motion.div 
							className="metric-value"
							initial={{ opacity: 0, scale: 0.5 }}
							animate={{ opacity: 1, scale: 1 }}
							transition={{ duration: 0.5, delay: 0.4 }}
						>
							{formatNumber(currentMetrics.currentMAU)}
						</motion.div>
						<div className="metric-change positive">
							{calculatePercentageChange(
								currentMetrics.currentMAU,
								currentMetrics.lastMonthMAU,
							)}
						</div>
					</div>
					<div className="metric-label">
						Monthly Active Users (MAU)
					</div>
					<div className="metric-chart">
						<LineChart
							data={monthlyData}
							dataKey="mau"
							height={60}
							color="#10b981"
							showGrid={false}
						/>
					</div>
				</motion.div>
				<ActiveUsersModal
					isOpen={isMauModalOpen}
					onClose={() => setIsMauModalOpen(false)}
					activeUsers={mauUsers}
					modalType="MAU"
					isLoading={isModalLoading}
				/>

				<ActiveUsersModal
					isOpen={isTimeSpecificModalOpen}
					onClose={() => setIsTimeSpecificModalOpen(false)}
					activeUsers={timeSpecificUsers}
					modalType="TIME_SPECIFIC"
					selectedTimePoint={selectedTimePoint}
					isLoading={isModalLoading}
				/>

				{/* Interactions Card */}
				<motion.div 
					className="metric-card info"
					variants={cardVariants}
					whileHover="hover"
				>
					<div className="metric-header">
						<motion.div 
							className="metric-value"
							initial={{ opacity: 0, scale: 0.5 }}
							animate={{ opacity: 1, scale: 1 }}
							transition={{ duration: 0.5, delay: 0.5 }}
						>
							{formatNumber(currentMetrics.currentInteractions)}
						</motion.div>
						<div className="metric-change positive">
							{calculatePercentageChange(
								currentMetrics.currentInteractions,
								currentMetrics.lastMonthInteractions,
							)}
						</div>
					</div>
					<div className="metric-label">Monthly Interactions</div>
					<div className="metric-chart">
						<LineChart
							data={monthlyData}
							dataKey="interactions"
							height={60}
							color="#06b6d4"
							showGrid={false}
						/>
					</div>
				</motion.div>

				{/* DM Messages Card */}
				<motion.div 
					className="metric-card warning"
					variants={cardVariants}
					whileHover="hover"
				>
					<div className="metric-header">
						<motion.div 
							className="metric-value"
							initial={{ opacity: 0, scale: 0.5 }}
							animate={{ opacity: 1, scale: 1 }}
							transition={{ duration: 0.5, delay: 0.6 }}
						>
							{formatNumber(currentMetrics.currentDMs)}
						</motion.div>
						<div className="metric-change positive">
							{calculatePercentageChange(
								currentMetrics.currentDMs,
								currentMetrics.lastMonthDMs,
							)}
						</div>
					</div>
					<div className="metric-label">Monthly DM Messages</div>
					<div className="metric-chart">
						<LineChart
							data={monthlyData}
							dataKey="dmMessages"
							height={60}
							color="#f59e0b"
							showGrid={false}
						/>
					</div>
				</motion.div>
			</motion.div>
			{/* Main Charts Grid */}
			<motion.div 
				className="charts-grid"
				variants={containerVariants}
			>
				{/* 24-Hour Active Users Chart */}
				<motion.div 
					className="chart-card full-width"
					variants={cardVariants}
					whileHover={{ borderColor: 'rgba(99, 102, 241, 0.4)' }}
				>
					<div className="chart-header">
						<h3>Active Users (Last 24 Hours)</h3>
						<div className="chart-insights">
							<div className="time-range-selector">
								<button
									className={
										timeInterval === "5min" ? "active" : ""
									}
									onClick={() => setTimeInterval("5min")}
								>
									5M
								</button>
								<button
									className={
										timeInterval === "15min" ? "active" : ""
									}
									onClick={() => setTimeInterval("15min")}
								>
									15M
								</button>
								<button
									className={
										timeInterval === "30min" ? "active" : ""
									}
									onClick={() => setTimeInterval("30min")}
								>
									30M
								</button>
								<button
									className={
										timeInterval === "1hour" ? "active" : ""
									}
									onClick={() => setTimeInterval("1hour")}
								>
									1H
								</button>
							</div>
							<div className="insight-item">
								<span className="insight-label">Avg Active</span>
								<span className="insight-value">
									{formatDetailedNumber(
										Math.floor(
											hourlyData.reduce(
												(sum, hour) =>
													sum + hour.activeUsers,
												0,
											) / hourlyData.length,
										),
									)}
								</span>
							</div>
							<div className="insight-item">
								<span className="insight-label">Peak Active</span>
								<span className="insight-value">
									{formatDetailedNumber(
										Math.max(
											...hourlyData.map(
												(h) => h.activeUsers,
											),
										),
									)}
								</span>
							</div>
						</div>
					</div>
					{loadingHourlyData ? (
						<div className="loading-indicator">
							Loading hourly data...
						</div>
					) : (
						<HourlyActiveUsersChart 
							data={hourlyData} 
							onPointClick={fetchUsersAtTime}
						/>
					)}
				</motion.div>
				
				{/* DAU Trend Chart */}
				<motion.div 
					className="chart-card full-width"
					variants={cardVariants}
					whileHover={{ borderColor: 'rgba(99, 102, 241, 0.4)' }}
				>
					<div className="chart-header">
						<h3>Daily Active Users</h3>
						<div className="chart-insights">
							<div className="time-range-selector">
								<button
									className={dauTimeRange === "1month" ? "active" : ""}
									onClick={() => handleDauTimeRangeChange("1month")}
									disabled={isDauLoading}
								>
									1M
								</button>
								<button
									className={dauTimeRange === "3months" ? "active" : ""}
									onClick={() => handleDauTimeRangeChange("3months")}
									disabled={isDauLoading}
								>
									3M
								</button>
								<button
									className={dauTimeRange === "6months" ? "active" : ""}
									onClick={() => handleDauTimeRangeChange("6months")}
									disabled={isDauLoading}
								>
									6M
								</button>
							</div>
							<div className="insight-item">
								<span className="insight-label">Average DAU</span>
								<span className="insight-value">
									{formatDetailedNumber(
										Math.floor(
											dailyData.reduce(
												(sum, day) => sum + day.dau,
												0,
											) / dailyData.length,
										),
									)}
								</span>
							</div>
							<div className="insight-item">
								<span className="insight-label">Peak DAU</span>
								<span className="insight-value">
									{formatDetailedNumber(
										Math.max(
											...dailyData.map((d) => d.dau),
										),
									)}
								</span>
							</div>
						</div>
					</div>
					{isDauLoading ? (
						<div className="loading-indicator" style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
							<div className="loading-spinner"></div>
							<span style={{ marginLeft: '10px', color: 'var(--text-secondary)' }}>Loading DAU data...</span>
						</div>
					) : (
						<DAUChart data={dailyData} />
					)}
				</motion.div>

				{/* MAU Trend Chart */}
				<motion.div 
					className="chart-card"
					variants={cardVariants}
					whileHover={{ borderColor: 'rgba(99, 102, 241, 0.4)' }}
				>
					<div className="chart-header">
						<h3>Monthly Active Users Trend</h3>
						<div className="chart-insights">
							<div className="insight-item">
								<span className="insight-label">
									Average MAU
								</span>
								<span className="insight-value">
									{formatDetailedNumber(
										Math.floor(
											monthlyData.reduce(
												(sum, month) => sum + month.mau,
												0,
											) / monthlyData.length,
										),
									)}
								</span>
							</div>
							<div className="insight-item">
								<span className="insight-label">Peak MAU</span>
								<span className="insight-value">
									{formatDetailedNumber(
										Math.max(
											...monthlyData.map((d) => d.mau),
										),
									)}
								</span>
							</div>
						</div>
					</div>
					<MAUChart data={monthlyData} />
				</motion.div>

				{/* Workspace Activity Chart */}
				<motion.div 
					className="chart-card full-width"
					variants={cardVariants}
					whileHover={{ borderColor: 'rgba(99, 102, 241, 0.4)' }}
				>
					<div className="chart-header">
						<h3>Workspace Activity (Last 30 Days)</h3>
						<div className="chart-insights">
							<div className="insight-item">
								<span className="insight-label">Total Workspaces</span>
								<span className="insight-value">
									{workspaceActivityData.length > 0 ? workspaceActivityData[0].totalWorkspaces : 0}
								</span>
							</div>
							<div className="insight-item">
								<span className="insight-label">Avg Active/Day</span>
								<span className="insight-value">
									{workspaceActivityData.length > 0 
										? Math.round(
											workspaceActivityData.reduce((sum, item) => sum + item.workspacesWithActivity, 0) 
											/ workspaceActivityData.length
										)
										: 0
									}
								</span>
							</div>
						</div>
					</div>
					{loadingWorkspaceActivity ? (
						<div className="loading" style={{ height: '360px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
							Loading workspace activity data...
						</div>
					) : workspaceActivityData.length > 0 ? (
						<WorkspaceActivityChart 
							data={workspaceActivityData} 
							height={300} 
							onBarClick={fetchWorkspaceDetails}
						/>
					) : (
						<div style={{ height: '360px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-tertiary)' }}>
							No workspace activity data available
						</div>
					)}
				</motion.div>
				
				{/* Daily Interactions Chart */}
				<motion.div 
					className="chart-card full-width"
					variants={cardVariants}
					whileHover={{ borderColor: 'rgba(99, 102, 241, 0.4)' }}
				>
					<div className="chart-header">
						<h3>Daily Interactions</h3>
						<div className="time-range-selector">
							<button
								className={
									dailyInteractionsFilter === "all-time" ? "active" : ""
								}
								onClick={() => handleDailyInteractionsFilterChange("all-time")}
								disabled={isDailyInteractionsLoading}
							>
								All Time
							</button>
							<button
								className={
									dailyInteractionsFilter === "current-month" ? "active" : ""
								}
								onClick={() => handleDailyInteractionsFilterChange("current-month")}
								disabled={isDailyInteractionsLoading}
							>
								Current Month
							</button>
							<button
								className={
									dailyInteractionsFilter === "last-month" ? "active" : ""
								}
								onClick={() => handleDailyInteractionsFilterChange("last-month")}
								disabled={isDailyInteractionsLoading}
							>
								Last Month
							</button>
						</div>
					</div>
					<div className="chart-insights">
						<div className="insight-item">
							<span className="insight-label">Avg. DMs/day</span>
							<span className="insight-value">
								{dailyInteractionData.length > 0
									? Math.round(
											dailyInteractionData.reduce(
												(sum, day) => sum + day.dms,
												0,
											) / dailyInteractionData.length,
										)
									: 0}
							</span>
						</div>
						<div className="insight-item">
							<span className="insight-label">
								Avg. Knocks/day
							</span>
							<span className="insight-value">
								{dailyInteractionData.length > 0
									? Math.round(
											dailyInteractionData.reduce(
												(sum, day) => sum + day.knocks,
												0,
											) / dailyInteractionData.length,
										)
									: 0}
							</span>
						</div>
						<div className="insight-item">
							<span className="insight-label">
								Avg. Invites/day
							</span>
							<span className="insight-value">
								{dailyInteractionData.length > 0
									? Math.round(
											dailyInteractionData.reduce(
												(sum, day) => sum + day.invites,
												0,
											) / dailyInteractionData.length,
										)
									: 0}
							</span>
						</div>
					</div>
					{isDailyInteractionsLoading ? (
						<div className="loading-indicator">
							<div className="loading-spinner">⏳</div>
							<div className="loading-text">
								Loading {dailyInteractionsFilter === "all-time" ? "historical" : 
										dailyInteractionsFilter === "current-month" ? "current month" : 
										"last month"} interactions...
							</div>
						</div>
					) : (
						<DailyInteractionChart data={dailyInteractionData} height={400} />
					)}
				</motion.div>

				{/* Daily Transcripts Chart */}
				<motion.div
					className="chart-card full-width"
					variants={cardVariants}
					whileHover={{ borderColor: 'rgba(159, 122, 234, 0.4)' }}
				>
					<div className="chart-header">
						<h3>Daily Transcripts</h3>
						<div className="time-range-selector">
							<button
								className={transcriptsFilter === "all-time" ? "active" : ""}
								onClick={() => handleTranscriptsFilterChange("all-time")}
								disabled={isDailyTranscriptsLoading}
							>
								All Time
							</button>
							<button
								className={transcriptsFilter === "current-month" ? "active" : ""}
								onClick={() => handleTranscriptsFilterChange("current-month")}
								disabled={isDailyTranscriptsLoading}
							>
								Current Month
							</button>
							<button
								className={transcriptsFilter === "last-month" ? "active" : ""}
								onClick={() => handleTranscriptsFilterChange("last-month")}
								disabled={isDailyTranscriptsLoading}
							>
								Last Month
							</button>
						</div>
					</div>
					<div className="chart-insights">
						<div className="insight-item">
							<span className="insight-label">Avg. Transcripts/day</span>
							<span className="insight-value">
								{dailyTranscriptData.length > 0
									? Math.round(
											dailyTranscriptData.reduce(
												(sum, day) => sum + (day.transcripts || 0),
												0,
											) / dailyTranscriptData.length,
										)
									: 0}
							</span>
						</div>
						<div className="insight-item">
							<span className="insight-label">Total Transcripts</span>
							<span className="insight-value">
								{dailyTranscriptData.reduce(
									(sum, day) => sum + (day.transcripts || 0),
									0,
								)}
							</span>
						</div>
						<div className="insight-item">
							<span className="insight-label">Total Duration</span>
							<span className="insight-value">
								{dailyTranscriptData.length > 0
									? formatMinutes(
											dailyTranscriptData.reduce(
												(sum, day) => sum + (day.durationMinutes || 0),
												0,
											),
										)
									: "0m"}
							</span>
						</div>
						<div className="insight-item">
							<span className="insight-label">Total Words</span>
							<span className="insight-value">
								{dailyTranscriptData
									.reduce((sum, day) => sum + (day.words || 0), 0)
									.toLocaleString()}
							</span>
						</div>
					</div>
					{isDailyTranscriptsLoading ? (
						<div className="loading-indicator">
							<div className="loading-spinner">⏳</div>
							<div className="loading-text">
								Loading {transcriptsFilter === "all-time" ? "historical" :
										transcriptsFilter === "current-month" ? "current month" :
										"last month"} transcripts...
							</div>
						</div>
					) : (
						<DailyInteractionChart
							data={dailyTranscriptData}
							height={400}
							metrics={[{ key: "transcripts", label: "Transcripts", color: "#9F7AEA" }]}
							processRow={(item) => item}
							extraTooltipRows={(day) => [
								{ label: "Duration", value: formatMinutes(day.durationMinutes || 0) },
								{ label: "Words", value: (day.words || 0).toLocaleString() },
							]}
						/>
					)}
				</motion.div>

				{/* Daily Copilot Responses Chart */}
				<motion.div
					className="chart-card full-width"
					variants={cardVariants}
					whileHover={{ borderColor: 'rgba(56, 178, 172, 0.4)' }}
				>
					<div className="chart-header">
						<h3>Daily Copilot Responses</h3>
						<div className="time-range-selector">
							<button
								className={copilotFilter === "all-time" ? "active" : ""}
								onClick={() => handleCopilotFilterChange("all-time")}
								disabled={isDailyCopilotLoading}
							>
								All Time
							</button>
							<button
								className={copilotFilter === "current-month" ? "active" : ""}
								onClick={() => handleCopilotFilterChange("current-month")}
								disabled={isDailyCopilotLoading}
							>
								Current Month
							</button>
							<button
								className={copilotFilter === "last-month" ? "active" : ""}
								onClick={() => handleCopilotFilterChange("last-month")}
								disabled={isDailyCopilotLoading}
							>
								Last Month
							</button>
						</div>
					</div>
					<div className="chart-insights">
						<div className="insight-item">
							<span className="insight-label">Avg. Responses/day</span>
							<span className="insight-value">
								{dailyCopilotData.length > 0
									? Math.round(
											(dailyCopilotData.reduce(
												(sum, day) => sum + (day.copilotResponses || 0),
												0,
											) / dailyCopilotData.length) * 10
										) / 10
									: 0}
							</span>
						</div>
						<div className="insight-item">
							<span className="insight-label">Total Copilot Responses</span>
							<span className="insight-value">
								{dailyCopilotData
									.reduce((sum, day) => sum + (day.copilotResponses || 0), 0)
									.toLocaleString()}
							</span>
						</div>
					</div>
					{isDailyCopilotLoading ? (
						<div className="loading-indicator">
							<div className="loading-spinner">⏳</div>
							<div className="loading-text">
								Loading {copilotFilter === "all-time" ? "historical" :
										copilotFilter === "current-month" ? "current month" :
										"last month"} copilot responses...
							</div>
						</div>
					) : (
						<DailyInteractionChart
							data={dailyCopilotData}
							height={400}
							metrics={[{ key: "copilotResponses", label: "Copilot Responses", color: "#38B2AC" }]}
							processRow={(item) => item}
						/>
					)}
				</motion.div>

				{/* Daily Session Time Chart */}
				<motion.div 
					className="chart-card"
					variants={cardVariants}
					whileHover={{ borderColor: 'rgba(99, 102, 241, 0.4)' }}
				>
					<div className="chart-header">
						<h3>Daily Session Time</h3>
						<div className="chart-insights">
							<div className="insight-item">
								<span className="insight-label">
									AVERAGE TIME
								</span>
								<span className="insight-value">
									{formatTime(
										calculateAverageTime(dailySessionData),
									)}
								</span>
							</div>
							<div className="insight-item">
								<span className="insight-label">PEAK TIME</span>
								<span className="insight-value">
									{formatTime(
										calculatePeakTime(dailySessionData),
									)}
								</span>
							</div>
						</div>
					</div>
					<DailySessionChart
						data={dailySessionData}
						height={300}
						color="#8b5cf6"
					/>
				</motion.div>

				{/* Engagement Comparison Chart */}
				<motion.div 
					className="chart-card"
					variants={cardVariants}
					whileHover={{ borderColor: 'rgba(99, 102, 241, 0.4)' }}
				>
					<div className="chart-header">
						<h3>Engagement Metrics Comparison</h3>
						<div className="time-range-selector">
							<button
								className={
									timeRange === "6months" ? "active" : ""
								}
								onClick={() => setTimeRange("6months")}
							>
								6M
							</button>
							<button
								className={
									timeRange === "3months" ? "active" : ""
								}
								onClick={() => setTimeRange("3months")}
							>
								3M
							</button>
						</div>
					</div>
					<EngagementChart
						data={timeRange === "3months" ? monthlyData.slice(-3) : monthlyData}
						metrics={[
							"interactions",
							"dmMessages",
							"stackMessages",
						]}
						height={250}
					/>
				</motion.div>
			</motion.div>

			{/* Workspace Users Modal - using same modal as DAU/MAU */}
			<ActiveUsersModal
				isOpen={isWorkspaceModalOpen}
				onClose={() => setIsWorkspaceModalOpen(false)}
				activeUsers={workspaceUsers}
				modalType={workspaceModalType}
				selectedTimePoint={selectedWorkspaceDate}
				isLoading={isModalLoading}
			/>
			</motion.div>
		</div>
	);
};

export default WorkspaceAnalytics;
